import {Component, OnDestroy, OnInit, ChangeDetectorRef, ViewChild, ElementRef} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {HeaderService} from 'app/services/header/header.service';
import {ToastrService} from 'ngx-toastr';
import {environment} from 'environments/environment';
import {DocumentService} from '../../../../../services/document/document.service';
import {DocumentPreviewService, PreviewData} from '../../../../../services/document/document-preview.service';
import {WarehouseService} from '../../../../../services/warehouse/warehouse.service';
import {StockService} from '../../../../../services/stock/stock.service';
import {ProductsService} from '../../../../../services/products/products.service';
import {
    LcBonReceptionEntreeLigne,
    LogicielCaisseStockReceptionService
} from 'app/services/logiciel-caisse/logiciel-caisse-stock-reception.service';
import {LogicielCaisseStockGestionService} from 'app/services/logiciel-caisse/logiciel-caisse-stock-gestion.service';
import {StationService} from '../../../../../services/station.service';
import {ParametreDocumentSettingsService} from '../../../../../services/parametre-document/parametre-document-settings.service';
import { isTruthyTinyInt } from '../../../../../services/parametre-document/parametre-doc-api-normalize';
import { resolveDocumentProductMatrixDisplay } from '../../../../../services/parametre-document/document-product-line-display.util';
import {forkJoin, from, Observable, of, Subject, throwError} from 'rxjs';
import {catchError, concatMap, filter, finalize, map, switchMap, takeUntil, toArray} from 'rxjs/operators';
import html2canvas from 'html2canvas';
import {
    getHtml2CanvasOptionsForDocumentExport,
    saveCanvasAsPdfDocument
} from '../../../shared/document-pdf-export.util';
import {DateEcheanceFrequency} from '../../../../../models/document';
import {resolveEnterpriseDisplayName} from '../../../../../services/enterprise-context';
import {
    filterHeaderButtonsWhenConversionLocked,
    isSourceDocumentLockedByConversion,
    parseConvertedChildrenFromApiResponse
} from '../../../../../services/document/document-conversion-lock.util';

interface TvaSummary {
    rate: string;
    base: number;
    amount: number;
}

/** Bloc d’affichage produits approve : ligne simple ou matrice couleur × taille. */
interface ApproveProductDisplayBlock {
    type: 'single' | 'matrix';
    ligne?: any;
    libelle?: string;
    refFabricant?: string;
    refInterne?: string;
    couleurs?: string[];
    tailles?: string[];
    cellMap?: { [key: string]: any };
    totalQnt?: number;
    totalHT?: number;
    unite?: string;
    prixUnitaire?: number;
    tva?: number;
    remise?: number;
    stockSum?: number;
    ajoutLignes?: any[];
}

@Component({
    selector: 'app-approve-vente',
    templateUrl: './approve-vente.component.html',
    styleUrls: ['./approve-vente.component.scss']
})
export class ApproveVenteComponent implements OnInit, OnDestroy {
    @ViewChild('documentContainer') documentContainer!: ElementRef;
    private destroy$ = new Subject<void>();
    mode: 'validation' | 'apercu' = 'validation';
    file: string = environment.file_url;
    urlImages: string = environment.url_images;
    currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    stationId = this.currentUser?.stations?.[0]?.station_id || this.currentUser?.stations?.[0]?.id || this.currentUser?.station_id || 0;
    entreprise: any = {};
    banque: any = {};
    document: any = null;
    stationLogo: string = this.file; // Logo par défaut
    stationName: string = resolveEnterpriseDisplayName();
    documentCachetUrl: string | null = null;
    private paramDocAffProduitMatriciel = true;
    stationFooterCoordName = '--';
    stationFooterCoordEmail = '--';
    stationFooterCoordPhones = '--';
    documentId: number | null = null;
    isLoading = true;
    approveReferenceHeaderPending = false;
    isExportingPDF = false;
    modifiedClientFields: { [key: string]: boolean } = {
        nomClient: false,
        identifiant: false,
        numeroIdentification: false,
        adresse: false,
        email: false
    };
    modifiedFournisseurFields: { [key: string]: boolean } = {
        nomEntreprise: false,
        numeroIdentification: false,
        responsable: false,
        telephone: false,
        adresse: false
    };
    projetDisplay: { id_projet: string; titre: string } | null = null;
    tvaSummary: TvaSummary[] = [];
    approveProductBlocks: ApproveProductDisplayBlock[] = [];
    approveProductsReady = false;
    /** Document source : conversion vers un autre type déjà créée (destination active) → pas Annuler / brouillon. */
    sourceLockedByActiveConversion = false;
    totals = {
        totalHT: 0,
        totalTVA: 0,
        totalTTC: 0,
        timbreFiscal: 0.6,
        netAPayer: 0.6
    };
    primaryColor = '#134AD3'; // Couleur par défaut
    nbrDate: number = 1; // Nombre de jours/mois/années pour la date d'échéance
    dateEcheance: string = ''; // Date d'échéance calculée
    dateFrequency: DateEcheanceFrequency = DateEcheanceFrequency.JOURS; // Fréquence (jours, mois, annee)
    documentLabel: string = ''; // Label du document
    stockQuantities: Map<number, number> = new Map();
    documentColumnSettings: any = {
        reference: false,
        quantite: true,
        unite: false,
        taille: false,
        prixUnitaire: true,
        tva: true,
        remise: false,
        afficherMontantsTTC: false,
        afficherCoordonneesClients: true,
        afficherCodeBarres: true,
        afficherDateEcheance: false,
        afficherDateLivraison: true,
        afficherMontantTotal: true,
        aff_montant_ttc: false,
        aff_code_bar: true,
        aff_date_echeance: false,
        aff_date_liv: true,
        aff_montant_total: true,
        aff_coordonnees: true,
        prix_unitaire: true
    };

    private statusUpdateInProgress = false;

    private statutIdMap: { [key: string]: number } = {
        'Crée': 9,
        'Créé': 9,
        'Annulé': 6,
        'Accepté': 3,
        'Négociation': 8,
        'Brouillon': 1,
        'Valide': 10,
        'Livré': 11,
        'Payé': 5,
        'Non Payé': 12,
        'Non payé': 12,
        'Fermé': 13,
        'Reçu': 14,
        'Inventorié': 15,
        'Partiellement payé': 15,
        'Transféré': 17,
        'Non utilisé': 19,
        'Partiellement utilisé': 20,
        'Utilisé': 18
    };

    constructor(
        private headerService: HeaderService,
        private router: Router,
        private route: ActivatedRoute,
        private documentService: DocumentService,
        private toastr: ToastrService,
        private documentPreviewService: DocumentPreviewService,
        private warehouseService: WarehouseService,
        private stockService: StockService,
        private productsService: ProductsService,
        private lcStockReception: LogicielCaisseStockReceptionService,
        private lcStockGestion: LogicielCaisseStockGestionService,
        private stationService: StationService,
        private parametreDocumentSettingsService: ParametreDocumentSettingsService,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit(): void {
        const idParam = this.route.snapshot.queryParamMap.get('id');
        this.documentId = idParam ? +idParam : null;

        this.loadStationData();

        this.updateModeFromUrl();
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.updateModeFromUrl();
                this.loadDataBasedOnMode();
                if (this.mode === 'apercu') {
                    setTimeout(() => {
                        const previewData = this.documentPreviewService.getCurrentPreview();
                        if (previewData?.previewData) {
                            this.loadModifiedFields(previewData.previewData);
                            this.cdr.detectChanges();
                        } else if (this.documentId) {
                            this.loadModifiedFields();
                            this.cdr.detectChanges();
                        }
                    }, 200);
                }
            });

        this.documentPreviewService.previewData$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: PreviewData | null) => {
                this.handlePreviewData(data);
                if (data?.previewData) {
                    setTimeout(() => {
                        this.loadModifiedFields(data.previewData);
                        this.cdr.detectChanges();
                    }, 150);
                }
            });

        this.loadDataBasedOnMode();
    }

    private loadDataBasedOnMode(): void {
        if (this.mode === 'apercu') {
            const previewData = this.documentPreviewService.getCurrentPreview();
            if (previewData) {
                this.handlePreviewData(previewData);
                // Forcer le rechargement des champs modifiés même si les données sont identiques
                // pour s'assurer que les dernières modifications sont prises en compte
                setTimeout(() => {
                    this.loadModifiedFields(previewData.previewData);
                    this.cdr.detectChanges();
                }, 100);
            } else if (this.documentId) {
                this.loadDocument();
            } else {
                this.isLoading = false;
                this.setHeaderConfig();
            }
        } else if (this.documentId) {
            this.loadDocument();
        } else {
            this.isLoading = false;
            this.setHeaderConfig();
        }
    }

    private handlePreviewData(data: PreviewData | null): void {
        if (data?.isDraft && data?.previewData) {
            // Récupérer le documentId depuis les données de prévisualisation ou les query params
            const previewDocId = data.previewData.id || data.previewData.document_id;
            if (previewDocId) {
                this.documentId = previewDocId;
            } else {
                // Essayer de récupérer depuis les query params
                const idParam = this.route.snapshot.queryParamMap.get('id');
                if (idParam) {
                    this.documentId = +idParam;
                }
            }
            if (this.documentId) {
                this.approveReferenceHeaderPending = true;
            }
            this.approveProductsReady = false;
            this.approveProductBlocks = [];
            
            this.document = this.normalizePreviewData(data.previewData);
            this.ensurePreviewWarehouseDetails();
            
            setTimeout(() => {
                this.loadModifiedFields(data.previewData);
                this.cdr.detectChanges();
            }, 50);
            
            this.calculateTotalsFromDocument();
            this.prepareApproveProductsDisplay();
            
            this.isLoading = false;
            this.syncApproveReferenceFromServerIfNeeded();
            this.refreshConversionLockForSourceDocument(() => this.setHeaderConfig());
        } else if (this.mode === 'apercu' && !data) {
            // Si pas de données de prévisualisation, essayer de charger depuis l'ID
            if (this.documentId) {
                this.loadDocument();
            } else {
                this.isLoading = false;
                this.setHeaderConfig();
            }
        }
    }

    private updateModeFromUrl(): void {
        const isApercu = this.router.url.includes('/apercu');
        const newMode: 'validation' | 'apercu' = isApercu ? 'apercu' : 'validation';

        if (this.mode !== newMode) {
            this.mode = newMode;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onPrimaryColorChange(color: string): void {
        this.primaryColor = color;
        // Mettre à jour le document avec la nouvelle couleur
        this.updateDocumentParametrage();
        this.cdr.detectChanges();
    }

    // Méthode pour convertir hex en rgba avec opacité
    getColorWithOpacity(opacity: number = 1): string {
        const hex = this.primaryColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    private normalizePreviewData(data: any): any {
        const client = data.client_data;
        const fournisseur = data.fournisseur_data;
        const formatDate = (dateString: string) => {
            if (!dateString) {
                return null;
            }
            try {
                const date = new Date(dateString);
                return date.toISOString().split('T')[0];
            } catch (error) {
                return dateString;
            }
        };
        const normalizedData = {
            ...data,
            client_data: client ? {
                id: client.id,
                nom_client: typeof client.nom_client === 'string' ? client.nom_client : (client.nom_prenom || ''),
                nom_prenom: client.nom_prenom || client.nom_client || '--',
                identifiant_unique: client.identifiant_unique || '--',
                email: client.email || '--',
                adresse: client.adresse || '--',
                telephone: client.telephone || '--'
            } : null,

            fournisseur_data: fournisseur ? {
                id: fournisseur.id,
                nom_entreprise: fournisseur.nom_entreprise || '--',
                numero_identification_fiscale: fournisseur.numero_identification_fiscale || '--',
                responsable: fournisseur.responsable || '--',
                phone: fournisseur.phone || fournisseur.telephone || '--',
                email: fournisseur.email || '--',
                adresse: fournisseur.adresse || '--',
                telephone: fournisseur.telephone || fournisseur.phone || '--'
            } : null,

            lignesDocument: (data.lignes_document || data.lignesDocument || []).map((ligne: any) =>
                this.normalizeLigneForApproveDisplay(ligne)
            ),
            /** Si formatDateToLocalString renvoie null, conserver date / date_document pour le retour vers document. */
            date:
                this.formatDateToLocalString(data.date || data.date_document) ??
                (data.date ?? data.date_document ?? null),
            /** Ne pas utiliser data.date ici : sinon la livraison redevient identique à la date document au retour. */
            date_livraison:
                this.formatDateToLocalString(data.date_livraison) ??
                (data.date_livraison ?? null),
            date_document: formatDate(data.date_document) || formatDate(data.date) || data.date_document,
            ref_externe: data.ref_externe || data.reference || '',
            /** Toujours un tableau (aperçu document + formats API variés). */
            projets: this.normalizeProjetsArrayFromData(data)
        };
        return normalizedData;
    }

    loadDocument(): void {
        this.isLoading = true;
        this.approveReferenceHeaderPending = true;
        this.approveProductsReady = false;
        this.approveProductBlocks = [];
        this.documentPreviewService.clear();
        this.documentService.getDocumentById(this.documentId!).subscribe({
            next: (res) => {
                const api = res?.data || res;
                let doc: any = res?.data?.document || res?.data || res;

                const typeTitre = doc?.typeDocument?.titre || doc?.type_document?.titre;
                if (!doc.typeDocument && doc.type_document) {
                    doc.typeDocument = doc.type_document;
                }
                const isAchatDocument = ['COMMANDE FOURNISSEUR', 'BON RECEPTION', 'FACTURE FOURNISSEUR'].includes(
                    String(typeTitre || '').toUpperCase()
                );
                const isStockDocument = ['INVENTAIRE', 'BON TRANSFERT'].includes(typeTitre);

                if (res?.data?.client_data) {
                    doc.client_data = res.data.client_data;
                } else if (doc?.clients?.length > 0) {
                    doc.client_data = doc.clients[0];
                } else if (doc?.client) {
                    doc.client_data = doc.client;
                }

                if (res?.data?.fournisseur_data) {
                    // Si fournisseur_data existe déjà, s'assurer qu'il a les bonnes propriétés
                    const existingData = res.data.fournisseur_data;
                    console.log('🔵 [loadDocument] Fournisseur data depuis res.data.fournisseur_data (brut):', existingData);
                    
                    doc.fournisseur_data = {
                        id: existingData.id,
                        nom_entreprise: existingData.nom_entreprise || '--',
                        numero_identification_fiscale: existingData.numero_identification_fiscale || '--',
                        responsable: existingData.responsable || (existingData as any).contact || (existingData as any).nom_contact || '--',
                        phone: existingData.phone || existingData.telephone || '--',
                        email: existingData.email || '--',
                        adresse: existingData.adresse || existingData.adresse_entreprise || '--',
                        telephone: existingData.telephone || existingData.phone || '--'
                    };
                    console.log('✅ [loadDocument] Fournisseur data formaté depuis res.data.fournisseur_data:', doc.fournisseur_data);
                } else if (doc?.fornisseurs?.length > 0) {
                    const fournisseurApi = doc.fornisseurs[0];
                    console.log('🔵 [loadDocument] Fournisseur API brut:', fournisseurApi);
                    
                    // Gérer l'adresse : peut être une chaîne directe ou un tableau d'objets
                    let formattedAdresse = '--';
                    if (fournisseurApi.adresse && typeof fournisseurApi.adresse === 'string') {
                        // Si c'est déjà une chaîne formatée, l'utiliser directement
                        formattedAdresse = fournisseurApi.adresse;
                    } else if (Array.isArray(fournisseurApi.adresses) && fournisseurApi.adresses.length > 0) {
                        // Si c'est un tableau d'objets, formater le premier
                        formattedAdresse = this.formatAdresse(fournisseurApi.adresses[0]);
                    } else if (fournisseurApi.adresses && !Array.isArray(fournisseurApi.adresses)) {
                        // Si c'est un objet unique, le formater
                        formattedAdresse = this.formatAdresse(fournisseurApi.adresses);
                    }
                    
                    // Gérer le téléphone : peut être une chaîne directe ou un tableau d'objets
                    let formattedPhone = '--';
                    if (fournisseurApi.phone && typeof fournisseurApi.phone === 'string') {
                        // Si c'est déjà une chaîne formatée, l'utiliser directement
                        formattedPhone = fournisseurApi.phone;
                    } else if (Array.isArray(fournisseurApi.telephones) && fournisseurApi.telephones.length > 0) {
                        // Si c'est un tableau d'objets, formater le premier
                        formattedPhone = this.getTelephoneString(fournisseurApi.telephones);
                    } else if (fournisseurApi.telephone && typeof fournisseurApi.telephone === 'string') {
                        // Si c'est une chaîne directe
                        formattedPhone = fournisseurApi.telephone;
                    }
                    
                    // Gérer l'email : peut être une chaîne directe ou un tableau d'objets
                    let formattedEmail = '--';
                    if (fournisseurApi.email && typeof fournisseurApi.email === 'string') {
                        // Si c'est déjà une chaîne formatée, l'utiliser directement
                        formattedEmail = fournisseurApi.email;
                    } else if (Array.isArray(fournisseurApi.emails) && fournisseurApi.emails.length > 0) {
                        // Si c'est un tableau d'objets, formater le premier
                        formattedEmail = this.getEmailString(fournisseurApi.emails);
                    }
                    
                    console.log('🔵 [loadDocument] Adresse formatée:', formattedAdresse);
                    console.log('🔵 [loadDocument] Téléphone formaté:', formattedPhone);
                    console.log('🔵 [loadDocument] Email formaté:', formattedEmail);
                    
                    doc.fournisseur_data = {
                        id: fournisseurApi.id,
                        nom_entreprise: fournisseurApi.nom_entreprise || '--',
                        numero_identification_fiscale: fournisseurApi.numero_identification_fiscale || '--',
                        responsable: fournisseurApi.responsable || (fournisseurApi as any).contact || (fournisseurApi as any).nom_contact || '--',
                        phone: formattedPhone,
                        email: formattedEmail,
                        adresse: formattedAdresse,
                        telephone: formattedPhone
                    };
                    
                    console.log('✅ [loadDocument] Fournisseur data formaté:', doc.fournisseur_data);
                }

                if (isAchatDocument) {
                    doc.client_data = null;
                    delete doc.client_data;
                    delete doc.clients;
                } else if (isStockDocument) {
                    // Pour INVENTAIRE et BON TRANSFERT, supprimer client et fournisseur
                    doc.client_data = null;
                    doc.fournisseur_data = null;
                    delete doc.client_data;
                    delete doc.clients;
                    delete doc.fournisseur_data;
                    delete doc.fornisseurs;
                } else {
                    doc.fournisseur_data = null;
                    delete doc.fournisseur_data;
                    delete doc.fornisseurs;
                }

                const entrepriseApi = doc.entreprises?.[0];
                if (entrepriseApi) {
                    this.entreprise = {
                        nom: entrepriseApi.nom_entreprise || 'Shopi',
                        adresse: entrepriseApi.adresse || 'Adresse non définie',
                        ville: entrepriseApi.ville || '',
                        pays: entrepriseApi.pays || '',
                        matricule_fiscal: entrepriseApi.matricule_fiscale || '--'
                    };
                } else {
                    this.entreprise = {nom: 'Shopi', adresse: 'Adresse non définie', ville: '', pays: '', matricule_fiscal: '--'};
                }

                const banqueApi = doc.coordonneesBancaires?.[0];
                if (banqueApi) {
                    this.banque = {
                        nom_banque: banqueApi.banque || banqueApi.nom || 'Banque non définie',
                        rib: banqueApi.rib || '--'
                    };
                } else {
                    this.banque = {nom_banque: 'Banque non définie', rib: '--'};
                }

                // Gérer l'entrepôt : si entrepot_id existe mais pas l'objet entrepot, le charger
                // Si l'entrepôt est déjà présent dans la réponse, le conserver
                if (doc.entrepot_id && !doc.entrepot) {
                    // Charger l'entrepôt via l'API
                    this.loadEntrepot(doc.entrepot_id);
                } else if (doc.entrepot) {
                    // L'entrepôt est déjà présent, s'assurer qu'il a la bonne structure
                    if (typeof doc.entrepot === 'object' && !doc.entrepot.nom) {
                        // Si c'est un objet mais sans nom, essayer de charger depuis l'ID
                        const entrepotId = doc.entrepot.id || doc.entrepot_id;
                        if (entrepotId) {
                            this.loadEntrepot(entrepotId);
                        }
                    }
                }

                // Gérer les entrepôts pour BON TRANSFERT (initial et destination)
                if (isStockDocument && typeTitre === 'BON TRANSFERT') {
                    // Charger l'entrepôt initial si nécessaire
                    if (doc.entrepot_id && !doc.entrepot) {
                        this.loadEntrepot(doc.entrepot_id);
                    }
                    // Charger l'entrepôt destination si nécessaire
                    // Vérifier d'abord entrepot_dest_id (nouveau nom), puis entrepot_destination_id (ancien nom pour compatibilité)
                    const entrepotDestId = doc.entrepot_dest_id || doc.entrepot_destination_id || (doc.entrepot_destination?.id);
                    if (entrepotDestId && !doc.entrepot_destination) {
                        this.loadEntrepotDestination(entrepotDestId);
                    }
                }

                // Normaliser les lignes (code_bar, ref_interne, couleur, taille, reference pour stock)
                if (doc.lignesDocument || doc.lignes_document) {
                    const lignes = doc.lignesDocument || doc.lignes_document || [];
                    doc.lignesDocument = lignes.map((ligne: any) => this.normalizeLigneForApproveDisplay(ligne));
                }
                
                this.document = {...doc};
                if (doc.id != null && doc.id !== '') {
                    const nid = Number(doc.id);
                    if (!isNaN(nid) && nid > 0) {
                        this.documentId = nid;
                    }
                }
                
                // Charger color, label, date_echeance (fréquence) et nbr_date depuis parametrage_doc
                if (doc.parametrage_doc) {
                    if (doc.parametrage_doc.color) {
                        this.primaryColor = doc.parametrage_doc.color;
                    }
                    if (doc.parametrage_doc.label) {
                        this.documentLabel = doc.parametrage_doc.label;
                    }
                    // date_echeance contient maintenant la fréquence (jours/mois/années)
                    if (doc.parametrage_doc.date_echeance) {
                        const frequencyValue = doc.parametrage_doc.date_echeance as string;
                        if (frequencyValue === 'jours' || frequencyValue === DateEcheanceFrequency.JOURS) {
                            this.dateFrequency = DateEcheanceFrequency.JOURS;
                        } else if (frequencyValue === 'mois' || frequencyValue === DateEcheanceFrequency.MOIS) {
                            this.dateFrequency = DateEcheanceFrequency.MOIS;
                        } else if (frequencyValue === 'annee' || frequencyValue === 'années' || frequencyValue === DateEcheanceFrequency.ANNEE) {
                            this.dateFrequency = DateEcheanceFrequency.ANNEE;
                        } else {
                            this.dateFrequency = DateEcheanceFrequency.JOURS; // Valeur par défaut
                        }
                    }
                    // nbr_date est maintenant dans parametrage_doc
                    if (doc.parametrage_doc.nbr_date !== undefined && doc.parametrage_doc.nbr_date !== null) {
                        this.nbrDate = doc.parametrage_doc.nbr_date;
                    }
                }
                // Fallback: si nbr_date est encore au niveau racine (pour compatibilité)
                if (doc.nbr_date !== undefined && doc.nbr_date !== null && (!doc.parametrage_doc || doc.parametrage_doc.nbr_date === undefined)) {
                    this.nbrDate = doc.nbr_date;
                }
                
                // Log pour vérifier que les données du fournisseur sont bien présentes
                if (isAchatDocument && this.document.fournisseur_data) {
                    console.log('✅ [loadDocument] Document final avec fournisseur_data:', {
                        adresse: this.document.fournisseur_data.adresse,
                        phone: this.document.fournisseur_data.phone,
                        telephone: this.document.fournisseur_data.telephone,
                        email: this.document.fournisseur_data.email
                    });
                }
                
                // NOUVEAU : Charger les champs cachés depuis le backend
                const defaultClientFields = {
                    nomClient: false,
                    identifiant: false,
                    numeroIdentification: false,
                    adresse: false,
                    email: false
                };
                
                const defaultFournisseurFields = {
                    nomEntreprise: false,
                    numeroIdentification: false,
                    responsable: false,
                    telephone: false,
                    adresse: false
                };
                
                if (doc.hidden_client_fields && typeof doc.hidden_client_fields === 'object') {
                    this.modifiedClientFields = { ...defaultClientFields, ...doc.hidden_client_fields };
                    console.log('✅ [loadDocument] Champs client chargés depuis backend:', this.modifiedClientFields);
                } else {
                    this.modifiedClientFields = { ...defaultClientFields };
                }
                
                if (doc.hidden_fournisseur_fields && typeof doc.hidden_fournisseur_fields === 'object') {
                    this.modifiedFournisseurFields = { ...defaultFournisseurFields, ...doc.hidden_fournisseur_fields };
                    console.log('✅ [loadDocument] Champs fournisseur chargés depuis backend:', this.modifiedFournisseurFields);
                } else {
                    this.modifiedFournisseurFields = { ...defaultFournisseurFields };
                }
                
                // Charger les champs modifiés depuis localStorage (fallback)
                this.loadModifiedFields();
                
                // Charger parametrage_doc depuis le document
                this.loadParametrageDoc(doc);
                
                this.calculateTotalsFromDocument();
                this.prepareApproveProductsDisplay();

                if (!this.documentPreviewService.getCurrentPreview()?.isDraft) {
                    this.refreshConversionLockForSourceDocument(() => this.setHeaderConfig());
                } else {
                    this.setHeaderConfig();
                }
                this.isLoading = false;
                this.approveReferenceHeaderPending = false;
                this.cdr.markForCheck();
            },
            error: (err) => {
                this.toastr.error('Impossible de charger le document.');
                this.isLoading = false;
                this.approveReferenceHeaderPending = false;
                this.setHeaderConfig();
            }
        });
    }

    loadStationData(): void {
        if (!this.stationId) {
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
            const station = currentUser?.stations?.[0];
            this.stationId = station?.station_id || station?.id || currentUser?.station_id || 0;
        }

        if (!this.stationId) {
            console.warn('⚠️ [ApproveVenteComponent] stationId non défini, impossible de charger les données de la station');
            return;
        }

        this.loadParametreDocumentCachet();
        this.stationService.getStation(this.stationId).subscribe({
            next: (response) => {
                if (response && response.success && response.station) {
                    const station = response.station;
                    // Charger le logo depuis le backend avec url_images
                    if (station.logo) {
                        // Si le logo est une URL complète, l'utiliser tel quel
                        if (station.logo.startsWith('http://') || station.logo.startsWith('https://')) {
                            this.stationLogo = station.logo;
                        } else {
                            // Sinon, préfixer avec url_images depuis environment
                            const logoPath = station.logo.startsWith('/') ? station.logo : `/${station.logo}`;
                            this.stationLogo = `${this.urlImages}${logoPath}`;
                        }
                        console.log('✅ [ApproveVenteComponent] Logo depuis backend:', this.stationLogo);
                    }
                    // Charger le nom d'entreprise
                    this.stationName = resolveEnterpriseDisplayName(station.nom_entreprise || station.label);
                    this.applyStationFooterContact(station);
                    this.cdr.detectChanges();
                }
            },
            error: (error) => {
                console.error('❌ [ApproveVenteComponent] Erreur lors du chargement des données de la station:', error);
                this.stationFooterCoordName = '--';
                this.stationFooterCoordEmail = '--';
                this.stationFooterCoordPhones = '--';
            }
        });
    }

    /** Prénom / nom / email depuis coordonnees[0], numéros depuis telephones[].numero */
    private applyStationFooterContact(station: any): void {
        const coord = station?.coordonnees?.[0];
        const prenom = (coord?.prenom ?? '').toString().trim();
        const nom = (coord?.nom ?? '').toString().trim();
        const fullName = [prenom, nom].filter(Boolean).join(' ').trim();
        this.stationFooterCoordName = fullName || '--';
        const email = (coord?.email ?? station?.email ?? '').toString().trim();
        this.stationFooterCoordEmail = email || '--';
        const telList =
            Array.isArray(station?.telephones) && station.telephones.length
                ? station.telephones
                : Array.isArray(coord?.telephones)
                  ? coord.telephones
                  : [];
        const nums = telList
            .map((t: any) => (t?.numero ?? '').toString().trim())
            .filter(Boolean);
        this.stationFooterCoordPhones = nums.length ? nums.join(', ') : '--';
    }

    private loadParametreDocumentCachet(): void {
        if (!this.stationId) {
            this.documentCachetUrl = null;
            return;
        }
        this.parametreDocumentSettingsService.getByStation(this.stationId).subscribe({
            next: (bundle) => {
                const pd = bundle?.parametre_document;
                const raw = pd?.image_cachet ?? null;
                this.documentCachetUrl = this.resolveCachetDisplayUrl(raw);
                this.paramDocAffProduitMatriciel = isTruthyTinyInt(pd?.aff_produit_matriciel ?? 1);
                if (this.approveProductsReady) {
                    this.rebuildApproveProductBlocks();
                }
                this.cdr.markForCheck();
            },
            error: () => {
                this.documentCachetUrl = null;
                this.cdr.markForCheck();
            }
        });
    }

    useProductMatrixLineDisplay(): boolean {
        return resolveDocumentProductMatrixDisplay({
            stationAffProduitMatriciel: this.paramDocAffProduitMatriciel,
        });
    }

    private resolveCachetDisplayUrl(raw: string | null | undefined): string | null {
        if (raw == null || raw === '') {
            return null;
        }
        const s = String(raw).trim();
        if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) {
            return s;
        }
        const base = this.urlImages.replace(/\/$/, '');
        const path = s.startsWith('/') ? s : `/${s}`;
        return `${base}${path}`;
    }

    /**
     * Affichage des totaux : Total TTC = document.total_ttc, Net à payer = document.net_payer.
     * Si le document fournit total_ht, total_tva, timbre_fiscal on les utilise ; sinon calcul depuis les lignes.
     */
    calculateTotalsFromDocument(): void {
        const doc = this.document;
        const lignes = doc?.lignesDocument || doc?.lignes_document || [];

        const parseDocNum = (val: any): number => {
            if (val == null || val === '') return NaN;
            const n = typeof val === 'number' ? val : parseFloat(String(val).replace(/[^\d.,]/g, '').replace(',', '.'));
            return isNaN(n) ? NaN : n;
        };
        const fromDoc = (key: string): number => parseDocNum((doc as any)?.[key]);

        this.totals.timbreFiscal = fromDoc('timbre_fiscal');
        if (isNaN(this.totals.timbreFiscal)) this.totals.timbreFiscal = 0.6;

        if (!lignes || lignes.length === 0) {
            this.tvaSummary = [];
            this.totals.totalHT = fromDoc('total_ht') || 0;
            this.totals.totalTVA = fromDoc('total_tva') || 0;
            this.totals.totalTTC = fromDoc('total_ttc') || (this.totals.totalHT + this.totals.totalTVA);
            this.totals.netAPayer = fromDoc('net_payer') || (this.totals.totalTTC + this.totals.timbreFiscal);
            this.applyProjetDisplayFromDocument(doc);
            return;
        }

        this.totals.totalHT = (doc?.total_ht != null && doc?.total_ht !== '') ? fromDoc('total_ht') : lignes.reduce((sum: number, l: any) => sum + (parseFloat(l.prix_ht) || 0) * (parseFloat(l.qnt) || 0), 0);
        this.totals.totalTVA = (doc?.total_tva != null && doc?.total_tva !== '') ? fromDoc('total_tva') : lignes.reduce((sum: number, l: any) => {
            const prix = parseFloat(l.prix_ht) || 0, qnt = parseFloat(l.qnt) || 0, tva = parseFloat(l.tva) || 0;
            return sum + (prix * qnt * tva / 100);
        }, 0);
        this.totals.totalTTC = (doc?.total_ttc != null && doc?.total_ttc !== '') ? fromDoc('total_ttc') : (this.totals.totalHT + this.totals.totalTVA);
        this.totals.netAPayer = (doc?.net_payer != null && doc?.net_payer !== '') ? fromDoc('net_payer') : (this.totals.totalTTC + this.totals.timbreFiscal);

        const tvaMap = new Map<string, { base: number; amount: number }>();
        lignes.forEach((l: any) => {
            const tva = parseFloat(l.tva) || 0;
            const rate = tva.toString();
            const prix = parseFloat(l.prix_ht) || 0;
            const qnt = parseFloat(l.qnt) || 0;
            const base = prix * qnt;
            const amount = base * tva / 100;

            if (tvaMap.has(rate)) {
                const prev = tvaMap.get(rate)!;
                tvaMap.set(rate, {base: prev.base + base, amount: prev.amount + amount});
            } else {
                tvaMap.set(rate, {base, amount});
            }
        });

        this.tvaSummary = Array.from(tvaMap.entries()).map(([rate, {base, amount}]) => ({
            rate,
            base: parseFloat(base.toFixed(3)),
            amount: parseFloat(amount.toFixed(3))
        }));
        this.applyProjetDisplayFromDocument(doc);
    }

    /**
     * Unifie projets[] / projet / project (aperçu + API) en un tableau.
     */
    private normalizeProjetsArrayFromData(data: any): any[] {
        if (!data) {
            return [];
        }
        if (Array.isArray(data.projets) && data.projets.length > 0) {
            return data.projets;
        }
        if (data.projet && typeof data.projet === 'object' && !Array.isArray(data.projet)) {
            return [data.projet];
        }
        if (data.project && typeof data.project === 'object' && !Array.isArray(data.project)) {
            return [data.project];
        }
        return [];
    }

    /**
     * Met à jour l'affichage du projet (indépendamment des lignes produit).
     */
    private applyProjetDisplayFromDocument(doc: any): void {
        if (!doc) {
            this.projetDisplay = null;
            return;
        }
        const list = this.normalizeProjetsArrayFromData(doc);
        const projet = list[0];
        if (!projet) {
            this.projetDisplay = null;
            return;
        }
        const idRaw = projet.id_projet ?? projet.idProjet ?? projet.id_pivot ?? projet.projet_id;
        const idProjetStr = idRaw != null && String(idRaw).trim() !== ''
            ? String(idRaw)
            : (projet.id != null ? String(projet.id) : '');
        const titre = (projet.titre ?? projet.libelle ?? projet.nom ?? '').trim();
        this.projetDisplay = {
            id_projet: idProjetStr || `PROJ-${String(projet.id ?? 0).padStart(3, '0')}`,
            titre: titre || 'Projet inconnu'
        };
    }

    /** Page validation sous /vente/avoir/ ou /achat/avoir-fournisseur/ (conversion → avoir). */
    private isAvoirValidationRoute(): boolean {
        const url = this.router.url || '';
        return url.includes('/vente/avoir/') || url.includes('/achat/avoir-fournisseur/');
    }

    /** ID document pour les appels API (query id ou document chargé). */
    private getEffectiveDocumentId(): number | null {
        const raw = this.documentId ?? this.document?.id ?? this.document?.document_id;
        if (raw == null || raw === '') {
            return null;
        }
        const n = Number(raw);
        return !isNaN(n) && n > 0 ? n : null;
    }

    /**
     * Station valide pour PATCH statut. `stationId` à 0 est traité comme « non résolu »
     * (relecture depuis currentUser si besoin).
     */
    private resolveStationIdForRequest(): number | null {
        const positive = (v: unknown): number | null => {
            const n = Number(v);
            return !isNaN(n) && n > 0 ? n : null;
        };
        let sid = positive(this.stationId);
        if (sid != null) {
            return sid;
        }
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const station = currentUser?.stations?.[0];
        sid = positive(station?.station_id ?? station?.id ?? currentUser?.station_id);
        if (sid != null) {
            this.stationId = sid;
        }
        return sid;
    }

    private finalizeHeaderButtons<T extends { label?: string }>(buttons: T[]): T[] {
        return filterHeaderButtonsWhenConversionLocked(buttons, this.sourceLockedByActiveConversion);
    }

    private refreshConversionLockForSourceDocument(onDone?: () => void): void {
        const id = Number(this.documentId ?? this.document?.id);
        if (!Number.isFinite(id) || id <= 0) {
            this.sourceLockedByActiveConversion = false;
            onDone?.();
            return;
        }
        this.documentService
            .getDocumentsByOriginalId(id)
            .pipe(catchError(() => of(null)))
            .subscribe((res) => {
                const children = res ? parseConvertedChildrenFromApiResponse(res) : [];
                this.sourceLockedByActiveConversion = isSourceDocumentLockedByConversion(
                    children,
                    this.statutIdMap
                );
                onDone?.();
                this.cdr.markForCheck();
            });
    }

    setHeaderConfig(): void {
        const title =
            this.document?.typeDocument?.titre ||
            this.document?.type_document?.titre ||
            'Document';

        type ButtonType = 'primary' | 'secondary';

        const baseButtons = [{label: 'Retour', action: () => this.retour(), type: 'primary' as ButtonType}];

        if (this.mode === 'apercu') {
            this.headerService.setConfig({
                title: `Aperçu - ${title}`,
                buttons: [
                    {label: 'Mettre à jour', action: () => this.saveAndGoToShared(), type: 'primary'}
                ]
            });
            this.cdr.markForCheck();
            return;
        }

            const config = this.getDocumentConfig();
        const statutKey = this.getNormalizedStatutKey();
        const typeKey = this.resolveDocumentTypeKey();

        // Retour fournisseur : consultation uniquement (pas de boutons de changement de statut)
        if (typeKey === 'RETOUR FOURNISSEUR') {
            this.headerService.setConfig({
                title,
                buttons: [{label: 'Retour', action: () => this.retour(), type: 'primary' as ButtonType}]
            });
            this.cdr.markForCheck();
            return;
        }

        if (this.isStatutCree(statutKey)) {
            const buttons = [
                ...baseButtons,
                {
                    label: 'Annuler',
                    action: () => this.updateStatus('Annulé'),
                    type: 'secondary' as ButtonType
                }
            ];
            // COMMANDE CLIENT / FOURNISSEUR : Valider → Valide (Crée = Approuver depuis brouillon)
            // Autres types (ex. inventaire) : garder acceptStatut du config
            const forceValide =
                typeKey === 'COMMANDE CLIENT' || typeKey === 'COMMANDE FOURNISSEUR';
                buttons.push({
                label: config.acceptLabel,
                action: () =>
                    this.updateStatus(
                        (forceValide ? 'Valide' : config.acceptStatut) as any
                    ),
                    type: 'primary' as ButtonType
                });
            if (config.hasNegociation) {
                buttons.push({
                    label: 'Négocier',
                    action: () => this.updateStatus('Négociation'),
                    type: 'primary' as ButtonType
                });
            }
            this.headerService.setConfig({title, buttons: this.finalizeHeaderButtons(buttons)});
        } else if (statutKey === 'NEGOCIATION') {
                this.headerService.setConfig({
                    title,
                buttons: this.finalizeHeaderButtons([
                        ...baseButtons,
                        {label: 'Annuler', action: () => this.updateStatus('Annulé'), type: 'secondary' as ButtonType},
                        {
                        label: config.acceptLabel,
                        action: () => this.updateStatus(config.acceptStatut as any),
                            type: 'primary' as ButtonType
                        }
                ])
                });
        } else if (this.isStatutRecu(statutKey)) {
            // Bon de réception reçu : Retour + Annuler
                this.headerService.setConfig({
                    title,
                buttons: this.finalizeHeaderButtons([
                    ...baseButtons,
                    {label: 'Annuler', action: () => this.updateStatus('Annulé'), type: 'secondary' as ButtonType}
                ])
            });
        } else if (
            statutKey === 'ACCEPTE' ||
            statutKey === 'VALIDE' ||
            statutKey === 'LIVRE' ||
            statutKey === 'NON PAYE' ||
            statutKey === 'FERME' ||
            statutKey === 'INVENTORIE' ||
            statutKey === 'TRANSFERE' ||
            statutKey === 'PAYE' ||
            statutKey === 'PARTIELLEMENT PAYE' ||
            statutKey === 'NON UTILISE' ||
            statutKey === 'PARTIELLEMENT UTILISE' ||
            statutKey === 'UTILISE'
        ) {
                this.headerService.setConfig({
                    title,
                buttons: this.finalizeHeaderButtons([
                        ...baseButtons,
                        {label: 'Annuler', action: () => this.updateStatus('Annulé'), type: 'secondary' as ButtonType}
                ])
                });
        } else if (this.isStatutAnnule(statutKey)) {
            // BR : Retour uniquement (pas de remise en brouillon)
            if (this.resolveDocumentTypeKey() === 'BON RECEPTION') {
                this.headerService.setConfig({
                    title,
                    buttons: this.finalizeHeaderButtons([
                        {label: 'Retour', action: () => this.retour(), type: 'primary' as ButtonType}
                    ])
                });
            } else {
                this.headerService.setConfig({
                    title,
                    buttons: this.finalizeHeaderButtons([
                        {
                            label: 'Remettre en brouillon',
                            action: () => this.updateStatusToBrouillon(),
                            type: 'primary' as ButtonType
                        },
                        {label: 'Retour', action: () => this.retour(), type: 'primary' as ButtonType}
                    ])
                });
            }
        } else if (statutKey === 'BROUILLON') {
                if (this.isAvoirValidationRoute()) {
                    this.headerService.setConfig({
                        title,
                    buttons: this.finalizeHeaderButtons([
                            ...baseButtons,
                            {
                                label: 'Annuler',
                                action: () => this.updateStatus('Annulé'),
                                type: 'secondary' as ButtonType
                            }
                    ])
                    });
                } else {
                // Approuver → Non payé (facture / FF) ou Crée (autres)
                const typeKey = this.resolveDocumentTypeKey();
                const approveTarget =
                    typeKey === 'FACTURE' || typeKey === 'FACTURE FOURNISSEUR'
                        ? 'Non Payé'
                        : 'Crée';
                    const buttons = [
                        ...baseButtons,
                        {label: 'Annuler', action: () => this.updateStatus('Annulé'), type: 'secondary' as ButtonType},
                    {label: 'Apercevoir', action: () => this.goToApercu(), type: 'primary' as ButtonType},
                    {
                        label: 'Approuver',
                        action: () => this.updateStatus(approveTarget as any),
                        type: 'primary' as ButtonType
                    }
                ];
                this.headerService.setConfig({title, buttons: this.finalizeHeaderButtons(buttons)});
            }
                    } else {
            // Statut inconnu : au minimum Retour + Annuler (évite header vide)
            this.headerService.setConfig({
                title,
                buttons: this.finalizeHeaderButtons([
                    ...baseButtons,
                    {label: 'Annuler', action: () => this.updateStatus('Annulé'), type: 'secondary' as ButtonType}
                ])
            });
        }
        this.cdr.markForCheck();
    }

    /** Normalise un libellé statut/type (accents, casse, espaces). */
    private normalizeLabel(value: unknown): string {
        return String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .toUpperCase();
    }

    /** Clé statut normalisée, avec repli sur statut_id (ex. 9 = Crée). */
    private getNormalizedStatutKey(): string {
        const rawTitre =
            this.document?.statut?.titre ??
            (this.document as any)?.statutDocument?.titre ??
            (this.document as any)?.statut_document?.titre ??
            (typeof this.document?.statut === 'string' ? this.document.statut : '') ??
            '';
        const fromTitre = this.normalizeLabel(rawTitre);
        if (fromTitre) {
            return fromTitre;
        }
        const id = Number(this.document?.statut_id ?? this.document?.statut?.id);
        if (!Number.isFinite(id) || id <= 0) {
            return '';
        }
        const entry = Object.entries(this.statutIdMap).find(([, sid]) => Number(sid) === id);
        return entry ? this.normalizeLabel(entry[0]) : '';
    }

    /** Crée / Créé (titre ou statut_id = 9). */
    private isStatutCree(statutKey?: string): boolean {
        const key = statutKey ?? this.getNormalizedStatutKey();
        if (key === 'CREE') {
            return true;
        }
        const id = Number(this.document?.statut_id ?? this.document?.statut?.id);
        return id === 9 || id === Number(this.statutIdMap['Crée']) || id === Number(this.statutIdMap['Créé']);
    }

    /** Annulé (titre ou statut_id = 6). */
    private isStatutAnnule(statutKey?: string): boolean {
        const key = statutKey ?? this.getNormalizedStatutKey();
        if (key === 'ANNULE') {
            return true;
        }
        const id = Number(this.document?.statut_id ?? this.document?.statut?.id);
        return id === 6 || id === Number(this.statutIdMap['Annulé']);
    }

    /** Reçu (titre ou statut_id = 14). */
    private isStatutRecu(statutKey?: string): boolean {
        const key = statutKey ?? this.getNormalizedStatutKey();
        if (key === 'RECU') {
            return true;
        }
        const id = Number(this.document?.statut_id ?? this.document?.statut?.id);
        return id === 14 || id === Number(this.statutIdMap['Reçu']);
    }

    private getDocumentConfig(): {
        acceptLabel: string;
        acceptStatut: string;
        hasNegociation: boolean;
        approveStatut?: string;
    } {
        const type = this.resolveDocumentTypeKey();

        switch (type) {
            case 'DEVIS':
                return {
                    acceptLabel: 'Accepter',
                    acceptStatut: 'Accepté',
                    hasNegociation: true
                };

            case 'COMMANDE CLIENT':
                // Approuver (brouillon) → Crée ; Valider (après Crée) → Valide
                return {
                    acceptLabel: 'Valider',
                    acceptStatut: 'Valide',
                    hasNegociation: false
                };

            case 'BON DE LIVRAISON':
                return {
                    acceptLabel: 'Livrer',
                    acceptStatut: 'Livré',
                    hasNegociation: false
                };

            case 'FACTURE':
                return {
                    acceptLabel: 'Non payé',
                    acceptStatut: 'Non Payé',
                    hasNegociation: false,
                    approveStatut: 'Non Payé'
                };

            case 'BON DE SORTIE':
                return {
                    acceptLabel: 'Fermer',
                    acceptStatut: 'Fermé',
                    hasNegociation: false
                };

            case 'FACTURE FOURNISSEUR':
                return {
                    acceptLabel: 'Non payé',
                    acceptStatut: 'Non Payé',
                    hasNegociation: false
                };
            case 'COMMANDE FOURNISSEUR':
                return {
                    acceptLabel: 'Valider',
                    acceptStatut: 'Valide',
                    hasNegociation: false
                };

            case 'BON RECEPTION':
                return {
                    acceptLabel: 'Recu',
                    acceptStatut: 'Reçu',
                    hasNegociation: false
                };

            case 'INVENTAIRE':
                return {
                    acceptLabel: 'Valider',
                    acceptStatut: 'Inventorié',
                    hasNegociation: false
                };

            case 'BON TRANSFERT':
                return {
                    acceptLabel: 'Transférer',
                    acceptStatut: 'Transféré',
                    hasNegociation: false
                };

            case 'AVOIR':
            case 'AVOIR FOURNISSEUR':
                return {
                    acceptLabel: 'Non utilisé',
                    acceptStatut: 'Non utilisé',
                    hasNegociation: false
                };

            case 'RETOUR FOURNISSEUR':
                return {
                    acceptLabel: 'Non payé',
                    acceptStatut: 'Non Payé',
                    hasNegociation: false
                };

            default:
                return {
                    acceptLabel: 'Accepter',
                    acceptStatut: 'Accepté',
                    hasNegociation: true
                };
        }
    }

    /** Clé type document normalisée (API + URL). */
    private resolveDocumentTypeKey(): string {
        const raw = this.normalizeLabel(
            this.document?.typeDocument?.titre ||
                this.document?.type_document?.titre ||
                this.document?.documentType ||
                ''
        );
        const url = (this.router.url || '').toLowerCase();
        // URL d’abord pour les docs achat (évite confondre FACTURE / FACTURE FOURNISSEUR)
        if (url.includes('facture-fournisseur') || (raw.includes('FACTURE') && raw.includes('FOURNISSEUR'))) {
            return 'FACTURE FOURNISSEUR';
        }
        if (url.includes('commande-fournisseur') || (raw.includes('COMMANDE') && raw.includes('FOURNISSEUR'))) {
            return 'COMMANDE FOURNISSEUR';
        }
        if (url.includes('bon-reception') || (raw.includes('BON') && raw.includes('RECEPTION'))) {
            return 'BON RECEPTION';
        }
        if (url.includes('retour-fournisseur') || (raw.includes('RETOUR') && raw.includes('FOURNISSEUR'))) {
            return 'RETOUR FOURNISSEUR';
        }
        if (!raw) {
            return 'DEVIS';
        }
        if (raw.includes('COMMANDE') && raw.includes('CLIENT')) {
            return 'COMMANDE CLIENT';
        }
        if (raw.includes('BON') && raw.includes('LIVRAISON')) {
            return 'BON DE LIVRAISON';
        }
        if (raw.includes('BON') && raw.includes('SORTIE')) {
            return 'BON DE SORTIE';
        }
        if (raw.includes('BON') && raw.includes('TRANSFERT')) {
            return 'BON TRANSFERT';
        }
        if (raw === 'FACTURE' || (raw.includes('FACTURE') && !raw.includes('AVOIR'))) {
            return 'FACTURE';
        }
        if (raw.includes('AVOIR') && raw.includes('FOURNISSEUR')) {
            return 'AVOIR FOURNISSEUR';
        }
        if (raw.includes('AVOIR')) {
            return 'AVOIR';
        }
        if (raw.includes('INVENTAIRE')) {
            return 'INVENTAIRE';
        }
        if (raw.includes('DEVIS')) {
            return 'DEVIS';
        }
        return raw;
    }

    updateStatus(
        statutLabel:
            | 'Crée'
            | 'Accepté'
            | 'Annulé'
            | 'Négociation'
            | 'Brouillon'
            | 'Non Payé'
            | 'Valide'
            | 'Livré'
            | 'Fermé'
            | 'Inventorié'
            | 'Transféré'
            | 'Reçu'
            | 'Non utilisé'
    ): void {
        if (this.statusUpdateInProgress) {
            return;
        }
        const labelKey = String(statutLabel);
        const statutId =
            this.statutIdMap[labelKey] ??
            this.statutIdMap[labelKey === 'Crée' ? 'Créé' : labelKey === 'Créé' ? 'Crée' : labelKey] ??
            this.statutIdMap[labelKey === 'Non Payé' ? 'Non payé' : labelKey === 'Non payé' ? 'Non Payé' : labelKey];
        const docId = this.getEffectiveDocumentId();
        const stationId = this.resolveStationIdForRequest();
        if (statutId == null || docId == null || stationId == null) {
            this.toastr.error('Données manquantes (ID ou station).');
            return;
        }
        const previousStatut = this.getDocumentStatutTitre();
        const payload = {
            statut_id: statutId,
            station_id: stationId
        };

        this.statusUpdateInProgress = true;
        this.documentService.updateStatut(docId, payload).subscribe({
            next: (res) => {
                this.statusUpdateInProgress = false;
                this.toastr.success(`Document mis à jour : ${statutLabel}`, 'Succès');
                this.documentId = docId;
                const apiStatut =
                    res?.data?.nouveau_statut ??
                    res?.data?.statut ??
                    res?.data?.document?.statut ??
                    res?.statut;
                if (this.document) {
                    // Toujours appliquer le statut demandé (évite de rester sur Crée si l’API renvoie un titre ambigu)
                        const titreFromDb =
                            labelKey === 'Non Payé' || labelKey === 'Non payé'
                                ? 'Non payé'
                                : labelKey === 'Créé'
                                  ? 'Crée'
                              : labelKey === 'Valide'
                                ? 'Valide'
                                  : labelKey;
                    if (apiStatut && typeof apiStatut === 'object' && (apiStatut as any).titre != null) {
                        const apiId = Number((apiStatut as any).id);
                        if (Number.isFinite(apiId) && apiId === statutId) {
                            this.document.statut = apiStatut;
                            this.document.statut_id = apiId;
                        } else {
                        this.document.statut = {...(this.document.statut || {}), titre: titreFromDb, id: statutId};
                        this.document.statut_id = statutId;
                    }
                    } else {
                        this.document.statut = {...(this.document.statut || {}), titre: titreFromDb, id: statutId};
                        this.document.statut_id = statutId;
                    }
                }
                /**
                 * Rester sur approve-vente :
                 * - Facture / FF Non payé (Retour / Annuler)
                 * - Annulé (CC, Facture, FF) → Remettre en brouillon / Retour
                 * BR Reçu / Annulé → redirect shared immédiatement
                 * Autres docs achat : Valide → liste.
                 */
                const typeKey = this.resolveDocumentTypeKey();
                const isFactureOrFf =
                    typeKey === 'FACTURE' || typeKey === 'FACTURE FOURNISSEUR';
                const isFactureNonPaye =
                    isFactureOrFf &&
                    (labelKey === 'Non Payé' || labelKey === 'Non payé');
                const stayOnAnnule =
                    labelKey === 'Annulé' &&
                    (isFactureOrFf || typeKey === 'COMMANDE CLIENT');

                const isBrRecuOrAnnule =
                    typeKey === 'BON RECEPTION' &&
                    (labelKey === 'Reçu' || labelKey === 'Annulé');

                const isBlLivre =
                    typeKey === 'BON DE LIVRAISON' && labelKey === 'Livré';

                const isAchatDoc =
                    typeKey.includes('FOURNISSEUR') || typeKey === 'BON RECEPTION';

                const shouldRetourShared =
                    isBrRecuOrAnnule ||
                    isBlLivre ||
                    (isAchatDoc &&
                        !isFactureNonPaye &&
                        !stayOnAnnule &&
                        (labelKey === 'Reçu' || labelKey === 'Valide'));

                // Pas de flash des boutons avant redirection vers shared
                if (!shouldRetourShared) {
                this.setHeaderConfig();
                    this.cdr.detectChanges();
                }

                if (isFactureNonPaye || stayOnAnnule) {
                    return;
                }
                
                const finish = () => {
                    if (shouldRetourShared) {
                        this.retour();
                    }
                };

                /** Stock uniquement lors d’une vraie transition de statut (pas re-clic). */
                if (
                    this.isEnteringStockStatut(previousStatut, 'Inventorié') &&
                    statutLabel === 'Inventorié' &&
                    this.document?.typeDocument?.titre === 'INVENTAIRE'
                ) {
                    this.updateStockForInventaireAfterInventorie(docId);
                }

                if (
                    this.isEnteringStockStatut(previousStatut, 'Reçu') &&
                    statutLabel === 'Reçu' &&
                    typeKey === 'BON RECEPTION'
                ) {
                    this.updateStockForBonReceptionAfterRecu(docId, finish);
                    return;
                }

                if (
                    statutLabel === 'Livré' &&
                    typeKey === 'BON DE LIVRAISON' &&
                    this.normalizeLabel(previousStatut) !== 'LIVRE'
                ) {
                    this.updateStockForBonLivraisonAfterLivre(docId, finish);
                    return;
                }

                if (
                    this.isEnteringStockStatut(previousStatut, 'Transféré') &&
                    statutLabel === 'Transféré' &&
                    this.document?.typeDocument?.titre === 'BON TRANSFERT'
                ) {
                    this.transferStockForBonTransfertAfterTransfere(docId);
                }

                finish();
            },
            error: (err) => {
                this.statusUpdateInProgress = false;
                this.toastr.error(
                    err?.error?.message || 'Échec de la mise à jour du statut.',
                    'Erreur'
                );
            }
        });
    }

    private getDocumentStatutTitre(): string {
        return String(this.document?.statut?.titre ?? '').trim();
    }

    /** Vrai seulement si le document n’était pas déjà au statut cible. */
    private isEnteringStockStatut(previousStatut: string, targetStatut: string): boolean {
        return this.normalizeLabel(previousStatut) !== this.normalizeLabel(targetStatut);
    }

    /**
     * Date / référence pour les mouvements de stock (aligné sur document.component).
     */
    private getMouvementStockDateAndRefApprove(documentId: number): { formattedDate: string; referenceDoc: string | null } {
        const raw = this.document?.date || this.document?.date_document;
        let formattedDate = raw
            ? String(raw).split('T')[0]
            : new Date().toISOString().split('T')[0];
        if (!formattedDate.includes('T')) {
            formattedDate = `${formattedDate}T12:00:00Z`;
        }
        const referenceDoc =
            String(this.document?.ref_externe || this.document?.reference || '').trim() || null;
        return { formattedDate, referenceDoc };
    }

    /**
     * Reçu BR : +stock logiciel caisse par code-barres (idempotent via reference_document).
     */
    private updateStockForBonReceptionAfterRecu(documentId: number, onComplete?: () => void): void {
        const hidden = this.document?.hidden_fournisseur_fields;
        const fromHidden = Number(
            hidden && typeof hidden === 'object' ? (hidden as any).logiciel_caisse_entrepot_id : NaN
        );
        const lcEntrepotId =
            (Number.isFinite(fromHidden) && fromHidden > 0
                ? fromHidden
                : Number(this.document?.entrepot_id ?? this.document?.entrepot?.id)) || 0;
        if (!lcEntrepotId) {
            this.toastr.warning(
                "Entrepôt manquant : le stock n'a pas été mis à jour. Sélectionnez un entrepôt sur le bon de réception.",
                'Stock',
                { timeOut: 7000 }
            );
            onComplete?.();
            return;
        }

        const rawLignes = this.document?.lignesDocument || this.document?.lignes_document || [];
        const productLignes = (rawLignes || []).filter((ligne: any) => {
            const qnt = Number(ligne?.qnt ?? ligne?.quantity) || 0;
            return qnt > 0 && (ligne?.product_id || ligne?.code_barre || ligne?.code_bar || ligne?.ref_interne);
        });
    
        if (!productLignes.length) {
            this.toastr.warning('Aucune ligne produit : stock non alimenté.', 'Stock');
            onComplete?.();
                return;
            }

        const refDoc =
            String(this.document?.ref_externe || this.document?.reference || '').trim() ||
            `BON RECEPTION #${documentId}`;
    
        forkJoin(
            productLignes.map((ligne: any) => {
                const qnt = Number(ligne.qnt ?? ligne.quantity) || 0;
                const lineCb = String(ligne.code_barre ?? ligne.code_bar ?? '')
                    .replace(/\s/g, '')
                    .trim();
                const lineRef = String(ligne.ref_interne ?? ligne.reference ?? '').trim();
                const libelle = String(ligne.libelle ?? ligne.designation ?? '').trim() || null;
    
                if (lineCb && lineCb !== '0' && lineCb !== '-') {
                    return of({
                        code_barre: lineCb,
                        ref_interne: lineRef && lineRef !== '-' ? lineRef : null,
                        qnt,
                        libelle,
                    });
                }
    
                const productId = Number(ligne.product_id);
                if (!Number.isFinite(productId) || productId <= 0) {
                                return of({
                        code_barre: null,
                        ref_interne: lineRef && lineRef !== '-' ? lineRef : null,
                        qnt,
                        libelle,
                    });
                }
    
                return this.productsService.getDetailsProduct(productId).pipe(
                    map((product: any) => {
                        const p = product?.data || product || {};
                        const cb = String(p.code_barre ?? p.code_bar ?? '')
                            .replace(/\s/g, '')
                            .trim();
                        const ref = String(p.ref_interne || p.reference || lineRef || '').trim();
                        return {
                            code_barre: cb && cb !== '0' && cb !== '-' ? cb : null,
                            ref_interne: ref && ref !== '-' ? ref : null,
                            qnt,
                            libelle: libelle || String(p.nom || '').trim() || null,
                        };
                    }),
                    catchError(() =>
                        of({
                            code_barre: null,
                            ref_interne: lineRef && lineRef !== '-' ? lineRef : null,
                            qnt,
                            libelle,
                            })
                        )
                    );
            })
        )
            .pipe(
                switchMap((rows: Array<LcBonReceptionEntreeLigne | null>) => {
                    const lignes: LcBonReceptionEntreeLigne[] = (rows || []).filter(
                        (r): r is LcBonReceptionEntreeLigne =>
                            !!r &&
                            Number(r.qnt) > 0 &&
                            !!(r.code_barre || r.ref_interne)
                    );
                    if (!lignes.length) {
                        return throwError(
                            () =>
                                new Error(
                                    'Aucun code-barres / référence sur les lignes. Renseignez le code-barres produit.'
                                )
                        );
                    }
                    return this.lcStockReception.entreeByBarcode({
                        entrepot_id: lcEntrepotId,
                        reference_document: refDoc,
                        lignes,
                    });
                }),
                finalize(() => onComplete?.())
            )
            .subscribe({
                next: (res) => {
                    if ((res as any)?.already_applied) {
                        this.toastr.info(
                            'Stock déjà mis à jour pour cette réception (pas de double augmentation).',
                            'Stock'
                        );
                    } else {
                        this.toastr.success(
                            `Stock caisse augmenté pour ${(res as any)?.updated ?? 0} produit(s)`,
                            'Succès'
                        );
                    }
                    this.stockQuantities.clear();
                    if ((this as any).stockLoadingKeys?.clear) {
                        (this as any).stockLoadingKeys.clear();
                    }
                    setTimeout(() => this.loadStockQuantitiesForAllProducts(), 800);
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    let msg = '';
                    if (typeof err === 'string') {
                        msg = err;
                    } else if (err && typeof err === 'object' && (err as any).message) {
                        msg = String((err as any).message);
                    }
                    this.toastr.error(
                        msg || 'Échec de la mise à jour du stock à la réception',
                        'Erreur',
                        { timeOut: 12000 }
                    );
                },
            });
    }
    
    

    /**
     * Livré BL : −stock_physique et −stock_disponible en caisse (code-barres, idempotent via reference_document).
     */
    private updateStockForBonLivraisonAfterLivre(documentId: number, onComplete?: () => void): void {
        let lcEntrepotId = this.resolveLogicielCaisseEntrepotId();
        if (!lcEntrepotId) {
            const fromFilter = this.getApproveLcEntrepotIdsForStock();
            lcEntrepotId = fromFilter?.[0] ?? null;
        }
        if (!lcEntrepotId) {
            this.toastr.warning(
                'Entrepôt logiciel caisse manquant : le stock n\'a pas été mis à jour. Sélectionnez un entrepôt sur le bon de livraison.',
                'Stock',
                { timeOut: 7000 }
            );
            onComplete?.();
            return;
        }

        const rawLignes = this.document?.lignesDocument || this.document?.lignes_document || [];
        const productLignes = (rawLignes || []).filter((ligne: any) => {
            const qnt = Number(ligne?.qnt ?? ligne?.quantity) || 0;
            return qnt > 0 && (ligne?.product_id || ligne?.code_barre || ligne?.code_bar || ligne?.ref_interne);
        });

        if (!productLignes.length) {
            this.toastr.warning('Aucune ligne produit : stock non mis à jour.', 'Stock');
            onComplete?.();
                return;
            }

        const refDoc =
            String(this.document?.ref_externe || this.document?.reference || '').trim() ||
            `BON DE LIVRAISON #${documentId}`;

        forkJoin(
            productLignes.map((ligne: any) => {
                const qnt = Number(ligne.qnt ?? ligne.quantity) || 0;
                const lineCb = String(ligne.code_barre ?? ligne.code_bar ?? '')
                    .replace(/\s/g, '')
                    .trim();
                const lineRef = String(ligne.ref_interne ?? ligne.reference ?? '').trim();
                const libelle = String(ligne.libelle ?? ligne.designation ?? '').trim() || null;

                if (lineCb && lineCb !== '0' && lineCb !== '-') {
                    return of({
                        code_barre: lineCb,
                        ref_interne: lineRef && lineRef !== '-' ? lineRef : null,
                        qnt,
                        libelle,
                    });
                }

                const productId = Number(ligne.product_id);
                if (!Number.isFinite(productId) || productId <= 0) {
                                return of({
                        code_barre: null,
                        ref_interne: lineRef && lineRef !== '-' ? lineRef : null,
                        qnt,
                        libelle,
                    });
                }

                return this.productsService.getDetailsProduct(productId).pipe(
                    map((product: any) => {
                        const p = product?.data || product || {};
                        const cb = String(p.code_barre ?? p.code_bar ?? '')
                            .replace(/\s/g, '')
                            .trim();
                        const ref = String(p.ref_interne || p.reference || lineRef || '').trim();
                        return {
                            code_barre: cb && cb !== '0' && cb !== '-' ? cb : null,
                            ref_interne: ref && ref !== '-' ? ref : null,
                            qnt,
                            libelle: libelle || String(p.nom || '').trim() || null,
                        };
                    }),
                    catchError(() =>
                        of({
                            code_barre: null,
                            ref_interne: lineRef && lineRef !== '-' ? lineRef : null,
                            qnt,
                            libelle,
                            })
                        )
                    );
            })
        )
            .pipe(
                switchMap((rows: Array<LcBonReceptionEntreeLigne | null>) => {
                    const lignes: LcBonReceptionEntreeLigne[] = (rows || []).filter(
                        (r): r is LcBonReceptionEntreeLigne =>
                            !!r &&
                            Number(r.qnt) > 0 &&
                            !!(r.code_barre || r.ref_interne)
                    );
                    if (!lignes.length) {
                        return throwError(
                            () =>
                                new Error(
                                    'Aucun code-barres / référence sur les lignes. Renseignez le code-barres produit.'
                                )
                        );
                    }
                    return this.lcStockReception.sortieBonLivraisonByBarcode({
                        entrepot_id: lcEntrepotId,
                        reference_document: refDoc,
                        lignes,
                    });
                }),
                finalize(() => onComplete?.())
            )
            .subscribe({
                next: (res) => {
                    if (res?.already_applied) {
                        this.toastr.info(
                            'Stock déjà mis à jour pour ce bon de livraison (pas de double sortie).',
                            'Stock'
                        );
                    } else {
                        this.toastr.success(
                            `Stock caisse diminué pour ${res?.updated ?? 0} produit(s) (physique + disponible)`,
                            'Succès'
                        );
                    }
                    this.stockQuantities.clear();
                    if ((this as any).stockLoadingKeys?.clear) {
                        (this as any).stockLoadingKeys.clear();
                    }
                    setTimeout(() => this.loadStockQuantitiesForAllProducts(), 800);
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    let msg = '';
                    if (typeof err === 'string') {
                        msg = err;
                    } else if (err && typeof err === 'object' && (err as any).message) {
                        msg = String((err as any).message);
                    }
                    this.toastr.error(
                        msg || 'Échec de la mise à jour du stock à la livraison',
                        'Erreur',
                        { timeOut: 12000 }
                    );
                },
            });
    }

    /**
     * Ajustements de stock (delta inventaire) au passage « Inventorié » — aligné sur document.component (type INVENTAIRE).
     */
    private updateStockForInventaireAfterInventorie(documentId: number, onComplete?: () => void): void {
        const selectedWarehouseId = this.document?.entrepot_id;
        if (!selectedWarehouseId) {
            this.toastr.warning(
                'Entrepôt manquant : le stock n\'a pas été mis à jour.',
                '',
                { timeOut: 5000 }
            );
            onComplete?.();
            return;
        }

        const lignes = this.document?.lignesDocument || this.document?.lignes_document || [];
        const stockUpdates: Observable<any>[] = [];

        lignes.forEach((ligne: any) => {
            const qteRaw = ligne.qnt ?? ligne.quantity;
            if (!ligne.product_id || qteRaw === null || qteRaw === undefined) {
                return;
            }
            const quantite = Number(qteRaw) || 0;

            const productId = ligne.product_id;
            const prixVenteFromItem = Number(ligne.prix_ht || 0);

            const productObservable = this.productsService.getDetailsProduct(productId).pipe(
                switchMap((product: any) => {
                    const productData = product.data || product;
                    const prixAchat = parseFloat(productData.prix_unitaires || productData.prix_achat || 0);
                    let prixVente = prixVenteFromItem;

                    if (prixVente <= 0) {
                        if (productData.result_ht) {
                            prixVente = parseFloat(productData.result_ht);
                        } else if (productData.liste_prix) {
                            const listePrix = Array.isArray(productData.liste_prix)
                                ? productData.liste_prix[0]
                                : productData.liste_prix;
                            if (listePrix?.result_ht) {
                                prixVente = parseFloat(listePrix.result_ht);
                            } else if (listePrix?.montant) {
                                prixVente = parseFloat(listePrix.montant);
                            }
                        }
                        if (prixVente <= 0 && productData.prix_unitaires) {
                            prixVente = parseFloat(productData.prix_unitaires);
                        }
                    }

                    const refInterne =
                        productData.ref_interne ||
                        productData.reference ||
                        ligne.ref_interne ||
                        ligne.reference ||
                        '';
                    if (!refInterne) {
                        return of(null);
                    }

                    const findProductObservable = this.stockService.getProductByRefInterne(refInterne).pipe(
                        switchMap((existingProduct: any) => {
                            if (existingProduct && existingProduct.id) {
                                const stockProductId = existingProduct.id;
                                return this.stockService
                                    .updateProductPrices(stockProductId, prixAchat, prixVente, productId)
                                    .pipe(
                                        switchMap((updateResponse: any) => {
                                            const updatedStockProductId =
                                                updateResponse?.stockProductId ||
                                                updateResponse?.data?.id ||
                                                updateResponse?.id ||
                                                updateResponse?.product?.id ||
                                                stockProductId;
                                            return of(updatedStockProductId);
                                        }),
                                        catchError(() => of(stockProductId))
                                    );
                            }
                            return this.stockService
                                .createProduct({
                                    id: productId,
                                    ref_interne: refInterne,
                                    nom: productData.nom || ligne.libelle || '',
                                    prix_achat: prixAchat,
                                    prix_vente: prixVente
                                })
                                .pipe(
                                    switchMap((createResponse: any) => {
                                        const stockProductId =
                                            createResponse?.stockProductId ||
                                            createResponse?.data?.id ||
                                            createResponse?.id ||
                                            createResponse?.product?.id;
                                        if (!stockProductId) {
                                            return throwError(() => new Error('ID module stock manquant'));
                                        }
                                        return of(stockProductId);
                                    }),
                                    catchError((createError: any) => {
                                        if (createError.status === 400 && createError.error?.existing_product_id) {
                                            const existingStockProductId = createError.error.existing_product_id;
                                            return this.stockService
                                                .updateProductPrices(
                                                    existingStockProductId,
                                                    prixAchat,
                                                    prixVente,
                                                    productId
                                                )
                                                .pipe(
                                                    switchMap((updateResponse: any) => {
                                                        const u =
                                                            updateResponse?.stockProductId ||
                                                            updateResponse?.data?.id ||
                                                            updateResponse?.id ||
                                                            updateResponse?.product?.id ||
                                                            existingStockProductId;
                                                        return of(u);
                                                    }),
                                                    catchError(() => of(existingStockProductId))
                                                );
                                        }
                                        return throwError(() => createError);
                                    })
                                );
                        }),
                        catchError((error) => throwError(() => error))
                    );

                    return findProductObservable.pipe(
                        switchMap((stockProductId: number | null) => {
                            if (!stockProductId) {
                                return of({
                                    success: false,
                                    productId,
                                    error: 'ID du produit module stock manquant',
                                    step: 'stock_product_id_missing'
                                });
                            }
                            const finalStockProductId = stockProductId;
                            const { formattedDate, referenceDoc } =
                                this.getMouvementStockDateAndRefApprove(documentId);

                            return this.stockService
                                .getStockByProductAndEntrepot(finalStockProductId, selectedWarehouseId)
                                .pipe(
                                    catchError(() => of({ quantity: 0 })),
                                    switchMap((currentStock: any) => {
                                        const quantityStock = Number(
                                            currentStock?.quantity ?? currentStock?.qty ?? 0
                                        );
                                        const quantiteSaisie = quantite;
                                        const delta = quantiteSaisie - quantityStock;
                                        if (delta === 0) {
                                            return of({
                                                success: true,
                                                productId,
                                                productLocalId: finalStockProductId,
                                                response: null,
                                                skippedNoDelta: true
                                            });
                                        }
                                        const stockApres = quantityStock + delta;
                                        return this.stockService
                                            .createMouvementStock({
                                                product_id: finalStockProductId,
                                                entrepot_id: selectedWarehouseId,
                                                type: 'INVENTAIRE',
                                                quantite: delta,
                                                date: formattedDate,
                                                reference_document: referenceDoc,
                                                document_id: documentId || null,
                                                stock_avant: quantityStock,
                                                stock_apres: stockApres
                                            })
                                            .pipe(
                                                map((response: any) => ({
                                                    success: true,
                                                    productId,
                                                    productLocalId: finalStockProductId,
                                                    response
                                                })),
                                                catchError((error) =>
                                                    of({
                                                        success: false,
                                                        productId,
                                                        productLocalId: finalStockProductId,
                                                        error
                                                    })
                                                )
                                            );
                                    })
                                );
                        }),
                        catchError((error) =>
                            of({
                                success: false,
                                productId,
                                error: error?.error || error?.message || 'Erreur inconnue',
                                step: 'stock_update'
                            })
                        )
                    );
                }),
                catchError((error) =>
                    of({
                        success: false,
                        productId,
                        error: error?.error || error?.message || 'Erreur lors de la récupération du produit',
                        step: 'product_fetch'
                    })
                )
            );

            stockUpdates.push(productObservable);
        });

        if (stockUpdates.length === 0) {
            onComplete?.();
            return;
        }

        forkJoin(stockUpdates)
            .pipe(finalize(() => onComplete?.()))
            .subscribe({
                next: (results) => {
                    const successResults = results.filter((r: any) => {
                        if (r && typeof r === 'object' && 'success' in r) {
                            return r.success === true;
                        }
                        return r !== null && r !== undefined;
                    });
                    const errorResults = results.filter((r: any) => {
                        if (r && typeof r === 'object' && 'success' in r) {
                            return r.success === false;
                        }
                        return r === null || r === undefined;
                    });
                    const successCount = successResults.length;
                    const errorCount = errorResults.length;
                    if (errorCount > 0) {
                        errorResults.forEach((errorResult: any) => {
                            if (errorResult?.error) {
                                console.error(
                                    '[updateStockForInventaireAfterInventorie] Erreur produit:',
                                    errorResult.error
                                );
                            }
                        });
                    }
                    const lineCount = stockUpdates.length;
                    if (successCount === lineCount) {
                        this.toastr.success(`Stock mis à jour pour ${successCount} produit(s)`, 'Succès');
                    } else if (successCount > 0) {
                        this.toastr.warning(
                            `Stock partiellement mis à jour (${successCount}/${lineCount})`,
                            'Attention'
                        );
                    } else {
                        this.toastr.error('Aucune mise à jour de stock effectuée', 'Erreur');
                    }
                    this.stockQuantities.clear();
                    this.stockLoadingKeys.clear();
                    setTimeout(() => this.loadStockQuantitiesForAllProducts(), 800);
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    const errorMessage = error?.error?.message || error?.message || 'Erreur inconnue';
                    this.toastr.error(`Erreur lors de la mise à jour du stock: ${errorMessage}`, 'Erreur');
                }
            });
    }

    /**
     * Transferts entre entrepôts au passage « Transféré » — aligné sur document.component createMouvementsStockForBonTransfert$.
     */
    private transferStockForBonTransfertAfterTransfere(documentId: number, onComplete?: () => void): void {
        const doc = this.document;
        if (!doc || doc?.typeDocument?.titre !== 'BON TRANSFERT') {
            onComplete?.();
            return;
        }
        const entrepotInitialId = doc.entrepot_id;
        const entrepotDestinationId = doc.entrepot_dest_id ?? doc.entrepot_destination_id;
        if (!entrepotInitialId || !entrepotDestinationId) {
            this.toastr.warning(
                'Entrepôts source ou destination manquants : le stock n\'a pas été transféré.',
                '',
                { timeOut: 6000 }
            );
            onComplete?.();
            return;
        }

        const referenceDoc =
            String(doc.ref_externe || doc.reference || '').trim() || null;
        const lignes = doc.lignesDocument || doc.lignes_document || [];
        const perProduct: Observable<any>[] = [];

        lignes.forEach((ligne: any) => {
            const qteRaw = ligne.qnt ?? ligne.quantity;
            if (!ligne.product_id || qteRaw === null || qteRaw === undefined || Number(qteRaw) === 0) {
                return;
            }
            const productId = ligne.product_id;
            const quantite = Number(qteRaw);
            const prixVente = Number(ligne.prix_ht || 0);

            const productObservable = this.productsService.getDetailsProduct(productId).pipe(
                switchMap((product: any) => {
                    const productData = product.data || product;
                    const refInterne =
                        productData.ref_interne ||
                        productData.reference ||
                        ligne.ref_interne ||
                        ligne.reference ||
                        '';
                    const prixAchat = parseFloat(productData.prix_unitaires || productData.prix_achat || 0);
                    const prixVenteStock = prixVente > 0 ? prixVente : prixAchat > 0 ? prixAchat : 0.01;
                    if (!refInterne) {
                        return of(null);
                    }
                    return this.stockService.getProductByRefInterne(refInterne).pipe(
                        switchMap((stockProduct: any) => {
                            if (stockProduct?.id) {
                                return this.stockService
                                    .updateProductPrices(stockProduct.id, prixAchat, prixVenteStock, productId)
                                    .pipe(
                                        switchMap(() => of(stockProduct.id)),
                                        catchError(() => of(stockProduct.id))
                                    );
                            }
                            return this.stockService
                                .createProduct({
                                    id: productId,
                                    ref_interne: refInterne,
                                    nom: productData.nom || ligne.libelle || '',
                                    prix_achat: prixAchat,
                                    prix_vente: prixVenteStock
                                })
                                .pipe(
                                    switchMap((createResponse: any) => {
                                        const id =
                                            createResponse?.stockProductId ||
                                            createResponse?.data?.id ||
                                            createResponse?.id ||
                                            createResponse?.product?.id;
                                        return id != null ? of(id) : of(null);
                                    }),
                                    catchError((createError: any) => {
                                        if (createError?.status === 400 && createError?.error?.existing_product_id) {
                                            const existingId = createError.error.existing_product_id;
                                            return this.stockService
                                                .updateProductPrices(existingId, prixAchat, prixVenteStock, productId)
                                                .pipe(
                                                    switchMap(() => of(existingId)),
                                                    catchError(() => of(existingId))
                                                );
                                        }
                                        return of(null);
                                    })
                                );
                        }),
                        switchMap((stockProductId: number | null) => {
                            if (!stockProductId) {
                                return of(null);
                            }
                            return this.stockService
                                .transferStock({
                                    entrepot_initial_id: entrepotInitialId,
                                    entrepot_dest_id: entrepotDestinationId,
                                    product_id: stockProductId,
                                    quantity: quantite,
                                    document_id: documentId,
                                    reference_document: referenceDoc
                                })
                                .pipe(
                                    catchError((err) => {
                                        console.error('[Bon transfert approve] transferStock:', err);
                                        const msg =
                                            err?.error?.message || err?.message || 'Erreur transfert de stock';
                                        this.toastr.error(msg, 'Stock — transfert', { timeOut: 8000 });
                                        return of(null);
                                    })
                                );
                        }),
                        catchError(() => of(null))
                    );
                }),
                catchError(() => of(null))
            );
            perProduct.push(productObservable);
        });

        if (perProduct.length === 0) {
            onComplete?.();
            return;
        }

        from(perProduct)
            .pipe(
                concatMap((obs) => obs),
                toArray(),
                finalize(() => onComplete?.())
            )
            .subscribe({
                next: (results) => {
                    const okCount = results.filter((r) => r !== null && r !== undefined).length;
                    if (okCount === perProduct.length) {
                        this.toastr.success(
                            'Transfert de stock enregistré pour le bon.',
                            'Stock',
                            { timeOut: 3000 }
                        );
                    } else if (okCount > 0) {
                        this.toastr.warning(
                            'Bon de transfert : certains mouvements de stock n’ont pas pu être enregistrés.',
                            'Attention',
                            { timeOut: 6000 }
                        );
                    } else {
                        this.toastr.warning(
                            'Bon de transfert : aucun mouvement de stock enregistré.',
                            'Attention',
                            { timeOut: 6000 }
                        );
                    }
                    this.stockQuantities.clear();
                    this.stockLoadingKeys.clear();
                    setTimeout(() => this.loadStockQuantitiesForAllProducts(), 800);
                    this.cdr.detectChanges();
                },
                error: (err) => {
                    console.error('[Bon transfert approve] Flux transfert:', err);
                    this.toastr.warning(
                        'Erreur lors de l’enregistrement des mouvements de stock.',
                        'Attention',
                        { timeOut: 5000 }
                    );
                }
            });
    }

    retour(): void {
        // Conserver les données de l'aperçu (dont la date modifiée) pour les restaurer dans document au retour
        if (this.mode === 'apercu' && this.documentPreviewService.getCurrentPreview()?.isDraft && this.document) {
            const type = this.getEffectiveDocumentTypeForNavigation();
            this.documentPreviewService.setPreviewData({
                isDraft: true,
                previewData: this.document,
                mode: 'edit',
                documentType: type
            });
        }

        const basePath = this.getBasePath();
        this.router.navigate([basePath]);
    }

    /**
     * Après conversion facture → avoir, l’API peut encore exposer « FACTURE » alors que la route est /vente/avoir/.
     */
    private getEffectiveDocumentTypeForNavigation(): string {
        const url = this.router.url || '';
        if (url.includes('/vente/avoir/')) {
            return 'AVOIR';
        }
        if (url.includes('/achat/avoir-fournisseur/')) {
            return 'AVOIR FOURNISSEUR';
        }
        return this.document?.typeDocument?.titre || this.document?.documentType || 'DEVIS';
    }

    private getBasePath(): string {
        const url = this.router.url || '';
        if (url.includes('/vente/avoir/')) {
            return '/vente/avoir';
        }
        if (url.includes('/achat/avoir-fournisseur/')) {
            return '/achat/avoir-fournisseur';
        }
        const map: { [key: string]: string } = {
            'DEVIS': '/vente/devis',
            'COMMANDE CLIENT': '/vente/commande-client',
            'BON DE SORTIE': '/vente/bon-sortie',
            'BON DE LIVRAISON': '/vente/bon-livraison',
            'FACTURE': '/vente/facture',
            'AVOIR': '/vente/avoir',

            'COMMANDE FOURNISSEUR': '/achat/commande-fournisseur',
            'BON RECEPTION': '/achat/bon-reception',
            'FACTURE FOURNISSEUR': '/achat/facture-fournisseur',
            'AVOIR FOURNISSEUR': '/achat/avoir-fournisseur',

            'INVENTAIRE': '/inventaire',
            'BON TRANSFERT': '/bon-transfert',
        };
        return map[this.document?.typeDocument?.titre || 'DEVIS'] || '/vente/devis';
    }

    getFormattedLivraisonDate(): string {
        if (!this.document?.date_livraison) {
            return '--';
        }
        const date = new Date(this.document.date_livraison);
        return date.toLocaleDateString('fr-TN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }

    saveAndGoToShared(): void {
        const type = this.getEffectiveDocumentTypeForNavigation();
        const basePath = this.getBasePath();
        const slug = this.getSlug(type);

        if (this.documentPreviewService.getCurrentPreview()?.isDraft) {
            this.returnToDocumentWithData();
        } else if (this.documentId) {
            this.router.navigate([`${basePath}/edit-${slug}`], {
                queryParams: {id: this.documentId}
            });
        } else {
            this.router.navigate([`${basePath}/add-${slug}`]);
        }
    }

    private returnToDocumentWithData(): void {
        const type = this.getEffectiveDocumentTypeForNavigation();
        const basePath = this.getBasePath();
        const slug = this.getSlug(type);

        this.documentPreviewService.setPreviewData({
            isDraft: true,
            previewData: this.document,
            mode: 'edit',
            documentType: type
        });

        const docId = this.document?.id ?? this.document?.document_id ?? this.documentId;
        const queryParams = docId != null ? { id: docId } : {};
        this.router.navigate([`${basePath}/edit-${slug}`], { queryParams }).then(success => {
            if (!success) {
                this.documentPreviewService.clear();
            }
        });
    }

    private getSlug(type: string): string {
        const url = this.router.url || '';
        /** Même segment d’URL que facture (add-facture / edit-facture) mais sous /vente/avoir/. */
        if (url.includes('/vente/avoir/')) {
            return 'facture';
        }
        if (url.includes('/achat/avoir-fournisseur/')) {
            return 'avoir-fournisseur';
        }
        const map: { [key: string]: string } = {
            'DEVIS': 'devis',
            'COMMANDE CLIENT': 'commande-client',
            'BON DE SORTIE': 'bon-sortie',
            'BON DE LIVRAISON': 'bon-livraison',
            'FACTURE': 'facture',
            'AVOIR': 'facture',
            'COMMANDE FOURNISSEUR': 'commande-fournisseur',
            'BON RECEPTION': 'bon-reception',
            'FACTURE FOURNISSEUR': 'facture-fournisseur',
            'AVOIR FOURNISSEUR': 'avoir-fournisseur',
            'INVENTAIRE': 'inventaire',
            'BON TRANSFERT': 'bon-transfert',
        };
        return map[type] || 'devis';
    }

    private goToShared(id: number): void {
        const type = this.getEffectiveDocumentTypeForNavigation();
        const map: { [key: string]: string } = {
            'DEVIS': 'devis',
            'COMMANDE CLIENT': 'commande-client',
            'BON DE SORTIE': 'bon-sortie',
            'BON DE LIVRAISON': 'bon-livraison',
            'FACTURE': 'facture',
            'AVOIR': 'avoir',
            'COMMANDE FOURNISSEUR': 'commande-fournisseur',
            'BON RECEPTION': 'bon-reception',
            'FACTURE FOURNISSEUR': 'facture-fournisseur',
            'AVOIR FOURNISSEUR': 'avoir-fournisseur',
            'INVENTAIRE': 'inventaire',
            'BON TRANSFERT': 'bon-transfert',
        };
        const slug = map[type] || 'devis';
        if (type === 'INVENTAIRE') {
            this.router.navigate(['/inventaire']);
        } else if (type === 'BON TRANSFERT') {
            this.router.navigate(['/bon-transfert']);
        } else {
            this.router.navigate(['/shared', slug, id]);
        }
    }

    goToApercu(): void {
        const basePath = this.getBasePath();
        this.router.navigate([`${basePath}/action/apercu`], {
            queryParams: {id: this.documentId}
        }).then(() => {
            setTimeout(() => {
                this.mode = 'apercu';
                this.setHeaderConfig();
            }, 150);
        });
    }

    retourToValidation(): void {
        const basePath = this.getBasePath();
        this.router.navigate([`${basePath}/action/validation`], {
            queryParams: {id: this.documentId}
        });
    }

    showBrouillonButtons(): void {
        this.document.statut = {titre: 'Brouillon'};
        this.setHeaderConfig();
        this.toastr.info('Mode brouillon activé (édition possible).', 'Info');
    }

    updateStatusToBrouillon(): void {
        if (this.resolveDocumentTypeKey() === 'BON RECEPTION') {
            this.toastr.warning(
                'Un bon de réception ne peut pas être remis en brouillon.',
                'Action impossible'
            );
            return;
        }

        const docId = this.getEffectiveDocumentId();
        const stationId = this.resolveStationIdForRequest();
        if (docId == null || stationId == null) {
            this.toastr.error('Impossible de modifier le statut.');
            return;
        }

        const payload = {
            statut_id: 1,
            station_id: stationId
        };

        this.documentService.updateStatut(docId, payload).subscribe({
            next: () => {
                this.toastr.success('Document remis en brouillon', 'Succès');
                this.documentId = docId;
                if (this.document) {
                this.document.statut = { titre: 'Brouillon', id: 1 };
                this.document.statut_id = 1;
                }
                this.documentPreviewService.clear();

                const type = this.getEffectiveDocumentTypeForNavigation();
                const slug = this.getSlug(type);
                // Inventaire / bon transfert : routes dédiées
                if (type === 'INVENTAIRE') {
                    this.router.navigate(['/inventaire/edit-inventaire'], { queryParams: { id: docId } });
                    return;
                }
                if (type === 'BON TRANSFERT') {
                    this.router.navigate(['/bon-transfert/edit-bon-transfert'], { queryParams: { id: docId } });
                    return;
                }
                const basePath = this.getBasePath();
                this.router.navigate([`${basePath}/edit-${slug}`], {
                    queryParams: { id: docId }
                });
            },
            error: () => {
                this.toastr.error('Échec de la mise en brouillon', 'Erreur');
            }
        });
    }

    isAchat(): boolean {
        const type = this.document?.typeDocument?.titre;
        return ['COMMANDE FOURNISSEUR', 'BON RECEPTION', 'FACTURE FOURNISSEUR'].includes(type);
    }

    isStockDocument(): boolean {
        const type = this.document?.typeDocument?.titre;
        return ['INVENTAIRE', 'BON TRANSFERT'].includes(type);
    }

    isInventaire(): boolean {
        const type = this.document?.typeDocument?.titre;
        return type === 'INVENTAIRE';
    }

    entrepotValidation(): boolean {
        const type = String(this.document?.typeDocument?.titre || this.document?.type_document?.titre || '')
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        return [
            'BON DE LIVRAISON',
            'FACTURE',
            'AVOIR',
            'BON RECEPTION',
            'BON DE RECEPTION',
            'FACTURE FOURNISSEUR',
            'AVOIR FOURNISSEUR',
            'INVENTAIRE',
            'BON TRANSFERT'
        ].includes(type);
    }
    projetValidation(): boolean {
        const type=this.document?.typeDocument?.titre;
        return['DEVIS','COMMANDE CLIENT', 'BON DE LIVRAISON','FACTURE','BON DE SORTIE','COMMANDE FOURNISSEUR','FACTURE FOURNISSEUR','BON DE RECEPTION'].includes(type);
    }

    isCommande(): boolean {
        const type = this.document?.typeDocument?.titre;
        return ['COMMANDE FOURNISSEUR', 'COMMANDE CLIENT'].includes(type);

    }

    getParty(): any {
        return this.isAchat() ? this.document?.fournisseur_data : this.document?.client_data;
    }

    getPartyLabel(): string {
        return this.isAchat() ? 'Fournisseur' : 'Client';
    }

    getPartyName(): string {
        const party = this.getParty();
        if (!party) {
            return '--';
        }
        return this.isAchat() ? (party.nom_entreprise || '--') : (party.nom_client || party.nom_prenom || '--');
    }

    getPartyPhone(): string {
        return this.getParty()?.phone || this.getParty()?.telephone || '--';
    }

    getPartyEmail(): string {
        return this.getParty()?.email || '--';
    }

    getPartyAddress(): string {
        return this.getParty()?.adresse || '--';
    }

    getPartyIdFiscal(): string {
        if (this.isAchat()) {
            return this.document?.fournisseur_data?.numero_identification_fiscale || '--';
        } else {
            return this.document?.client_data?.identifiant_unique || '--';
        }
    }

    getPartyNumeroIdentification(): string {
        return (
            this.document?.client_data?.numero_identification_fiscale ||
            this.getParty()?.numero_identification_fiscale ||
            '--'
        );
    }

    getResponsable(): string {
        return this.document?.fournisseur_data?.responsable || '--';
    }

    getEntrepriseName(): string {
        return this.document?.fournisseur_data?.nom_entreprise || '--';
    }

    getAdresseEntreprise(): string {
        return this.document?.fournisseur_data?.adresse || '--';
    }

    getRefExterne(): string {
        return this.document?.ref_externe || '--';
    }

    isApproveReferenceLoading(): boolean {
        if (this.approveReferenceHeaderPending) {
            return true;
        }
        if (this.documentId && this.isLoading) {
            return true;
        }
        return false;
    }

    getApproveHeaderReference(): string {
        if (this.isApproveReferenceLoading()) {
            return '';
        }
        return this.resolveApproveReferenceString();
    }

    /** Référence pour export PDF / toasts (repli type-prefix si besoin). */
    getDisplayReference(): string {
        const ref = this.resolveApproveReferenceString();
        if (ref) {
            return ref;
        }
        const type = this.document?.typeDocument?.titre || this.document?.documentType || 'DEVIS';
        const prefixMap: { [key: string]: string } = {
            'DEVIS': 'DEV', 'COMMANDE CLIENT': 'CC', 'BON DE SORTIE': 'BS', 'BON DE LIVRAISON': 'BL',
            'FACTURE': 'FAC', 'COMMANDE FOURNISSEUR': 'CF', 'BON RECEPTION': 'BR', 'FACTURE FOURNISSEUR': 'FF',
            'INVENTAIRE': 'INV', 'BON TRANSFERT': 'BT', 'AVOIR': 'AVR', 'AVOIR FOURNISSEUR': 'AF'
        };
        const typeUpper = String(type).toUpperCase().trim();
        const prefix = prefixMap[typeUpper] || 'DOC';
        return `${prefix}-0001`;
    }

    private resolveApproveReferenceString(): string {
        const type = this.document?.typeDocument?.titre || this.document?.documentType || 'DEVIS';
        const prefixMap: { [key: string]: string } = {
            'DEVIS': 'DEV', 'COMMANDE CLIENT': 'CC', 'BON DE SORTIE': 'BS', 'BON DE LIVRAISON': 'BL',
            'FACTURE': 'FAC', 'COMMANDE FOURNISSEUR': 'CF', 'BON RECEPTION': 'BR', 'FACTURE FOURNISSEUR': 'FF',
            'INVENTAIRE': 'INV', 'BON TRANSFERT': 'BT', 'AVOIR': 'AVR', 'AVOIR FOURNISSEUR': 'AF'
        };
        const typeUpper = String(type).toUpperCase().trim();
        const expectedPrefix = prefixMap[typeUpper];
        let ref = String(this.document?.ref_externe || this.document?.reference || '').trim();
        if (ref && expectedPrefix && /^FAC(?=[-_]|$)/i.test(ref) && typeUpper.includes('AVOIR')) {
            ref = ref.replace(/^FAC/i, expectedPrefix);
        }
        if (ref && !/^0+$/.test(ref)) {
            return ref;
        }
        return '';
    }

    private syncApproveReferenceFromServerIfNeeded(): void {
        const id = this.documentId;
        if (!id || id <= 0) {
            this.approveReferenceHeaderPending = false;
            return;
        }
        this.approveReferenceHeaderPending = true;
        this.cdr.markForCheck();
        this.documentService.getDocumentById(id).subscribe({
            next: (res) => {
                const doc = res?.data?.document || res?.data || res;
                const refExterne = doc?.ref_externe ?? doc?.reference;
                const reference = doc?.reference ?? doc?.ref_externe;
                if (this.document && (refExterne || reference)) {
                    this.document = {
                        ...this.document,
                        ...(refExterne != null && refExterne !== '' && { ref_externe: refExterne }),
                        ...(reference != null && reference !== '' && { reference })
                    };
                }
                this.approveReferenceHeaderPending = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.approveReferenceHeaderPending = false;
                this.cdr.markForCheck();
            }
        });
    }

    /**
     * Mode aperçu : si le brouillon contient seulement entrepot_id (sans objet `entrepot` avec `nom`),
     * charge le détail comme pour loadDocument().
     */
    private ensurePreviewWarehouseDetails(): void {
        const doc = this.document;
        if (!doc) {
            return;
        }
        if (doc.entrepot_id && !doc.entrepot) {
            this.loadEntrepot(doc.entrepot_id);
        }
        const type = doc.typeDocument?.titre;
        if (type === 'BON TRANSFERT') {
            const destId = doc.entrepot_dest_id || doc.entrepot_destination_id;
            if (destId && !doc.entrepot_destination) {
                this.loadEntrepotDestination(destId);
            }
        }
    }

    private loadEntrepot(entrepotId: number): void {
        if (!entrepotId || !this.currentUser?.id || !this.stationId) {
            return;
        }

        this.warehouseService.detailEntrepot(entrepotId, this.currentUser.id, this.stationId).subscribe({
            next: (response: any) => {
                const entrepotData = response?.entrepot || response?.data || response;
                if (entrepotData) {
                    // Mettre à jour le document avec l'entrepôt chargé
                    if (this.document) {
                        this.document.entrepot = entrepotData;
                        if (!this.document.entrepot_id) {
                            this.document.entrepot_id = entrepotId;
                        }
                        if (this.shouldFilterStockByEntrepot()) {
                            this.stockQuantities.clear();
                            this.stockLoadingKeys.clear();
                            this.loadStockQuantitiesForAllProducts();
                        }
                        this.cdr.detectChanges();
                    }
                }
            },
            error: (err) => {
                console.error('Erreur lors du chargement de l\'entrepôt:', err);
                // Ne pas afficher d'erreur à l'utilisateur, juste logger
            }
        });
    }

    getEntrepot(): string {
        if (!this.document?.entrepot) {
            // Si entrepot_id existe mais pas l'objet entrepot, retourner un message temporaire
            if (this.document?.entrepot_id) {
                return 'Chargement...';
            }
            return '--';
        }
        return typeof this.document.entrepot === 'string'
            ? this.document.entrepot
            : this.document.entrepot?.nom || '--';
    }

    getEntrepotInitial(): string {
        // Pour BON TRANSFERT, utiliser entrepot_id comme entrepôt initial
        if (this.document?.typeDocument?.titre === 'BON TRANSFERT') {
            if (!this.document?.entrepot) {
                if (this.document?.entrepot_id) {
                    return 'Chargement...';
                }
                return '--';
            }
            return typeof this.document.entrepot === 'string'
                ? this.document.entrepot
                : this.document.entrepot?.nom || '--';
        }
        // Pour INVENTAIRE, utiliser le même que getEntrepot()
        return this.getEntrepot();
    }

    getEntrepotDestination(): string {
        // Pour BON TRANSFERT, utiliser entrepot_dest_id
        if (this.document?.typeDocument?.titre === 'BON TRANSFERT') {
            if (!this.document?.entrepot_destination) {
                // Vérifier d'abord entrepot_dest_id (nouveau nom), puis entrepot_destination_id (ancien nom pour compatibilité)
                const entrepotDestId = this.document?.entrepot_dest_id || this.document?.entrepot_destination_id || (this.document?.entrepot_destination?.id);
                if (entrepotDestId) {
                    // Charger l'entrepôt destination si nécessaire
                    this.loadEntrepotDestination(entrepotDestId);
                    return 'Chargement...';
                }
                return '--';
            }
            return typeof this.document.entrepot_destination === 'string'
                ? this.document.entrepot_destination
                : this.document.entrepot_destination?.nom || '--';
        }
        return '--';
    }

    private loadEntrepotDestination(entrepotId: number): void {
        if (!entrepotId || !this.currentUser?.id || !this.stationId) {
            return;
        }

        this.warehouseService.detailEntrepot(entrepotId, this.currentUser.id, this.stationId).subscribe({
            next: (response: any) => {
                const entrepotData = response?.entrepot || response?.data || response;
                if (entrepotData && this.document) {
                    this.document.entrepot_destination = entrepotData;
                    this.cdr.detectChanges();
                }
            },
            error: (err) => {
                console.error('Erreur lors du chargement de l\'entrepôt destination:', err);
            }
        });
    }

    getCategories(): string {
        const cats = this.document?.categories;
        if (cats && Array.isArray(cats) && cats.length > 0) {
            return cats
                .map((c: any) => c.nom || c.titre || c.libelle)
                .filter((s: string) => s != null && String(s).trim() !== '')
                .join(', ');
        }
        const lib =
            this.document?.categorie_libelle ??
            (typeof this.document?.categorie === 'string' ? this.document.categorie : null);
        if (lib != null && String(lib).trim() !== '') {
            return String(lib).trim();
        }
        return '--';
    }

    getFormattedDate(): string {
        if (!this.document?.date) {
            return '--';
        }
        const date = new Date(this.document.date);
        return date.toLocaleDateString('fr-TN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }

    private formatDateToLocalString(dateString: string | null): string | null {
        if (!dateString) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
        }

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return null;

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private formatAdresse(addr: any): string {
        if (!addr) {
            return 'Adresse non renseignée';
        }
        return `${addr.adresse || ''}, ${addr.code_postal || ''} ${addr.region || ''}, ${addr.pays || ''}`.trim() || 'Adresse non renseignée';
    }

    private getTelephoneString(telephones?: any[]): string {
        if (!telephones || telephones.length === 0) {
            return '--';
        }
        const t = telephones[0];
        return `${t.code_pays || '+216'}${t.num_tel || '00000000'}`;
    }

    private getEmailString(emails?: any[]): string {
        return emails?.[0]?.email || '--';
    }

    // Charger les champs modifiés depuis localStorage
    private loadModifiedFields(previewData?: any): void {
        // Valeurs par défaut
        const defaultClientFields = {
            nomClient: false,
            identifiant: false,
            numeroIdentification: false,
            adresse: false,
            email: false
        };
        
        const defaultFournisseurFields = {
            nomEntreprise: false,
            numeroIdentification: false,
            responsable: false,
            telephone: false,
            adresse: false
        };
        
        // Priorité 1 : Charger depuis le document si disponible (backend)
        if (this.document?.hidden_client_fields && typeof this.document.hidden_client_fields === 'object') {
            this.modifiedClientFields = { ...defaultClientFields, ...this.document.hidden_client_fields };
            console.log('✅ [loadModifiedFields] Champs client chargés depuis document (backend)');
        } else if (previewData?.hidden_client_fields && typeof previewData.hidden_client_fields === 'object') {
            this.modifiedClientFields = { ...defaultClientFields, ...previewData.hidden_client_fields };
            console.log('✅ [loadModifiedFields] Champs client chargés depuis previewData');
        } else {
            this.modifiedClientFields = { ...defaultClientFields };
        }
        
        if (this.document?.hidden_fournisseur_fields && typeof this.document.hidden_fournisseur_fields === 'object') {
            this.modifiedFournisseurFields = { ...defaultFournisseurFields, ...this.document.hidden_fournisseur_fields };
            console.log('✅ [loadModifiedFields] Champs fournisseur chargés depuis document (backend)');
        } else if (previewData?.hidden_fournisseur_fields && typeof previewData.hidden_fournisseur_fields === 'object') {
            this.modifiedFournisseurFields = { ...defaultFournisseurFields, ...previewData.hidden_fournisseur_fields };
            console.log('✅ [loadModifiedFields] Champs fournisseur chargés depuis previewData');
        } else {
            this.modifiedFournisseurFields = { ...defaultFournisseurFields };
        }
        
        // Fallback : Essayer localStorage si pas de données backend et que les champs sont tous à false
        if (this.documentId && Object.values(this.modifiedClientFields).every(v => v === false) && 
            Object.values(this.modifiedFournisseurFields).every(v => v === false)) {
            const key = `modifiedFields_doc_${this.documentId}`;
            const saved = localStorage.getItem(key);
            if (saved) {
                try {
                    const data = JSON.parse(saved);
                    if (data.clientFields) {
                        if (Array.isArray(data.clientFields)) {
                            const clientFieldsObj: { [key: string]: boolean } = { ...defaultClientFields };
                            data.clientFields.forEach((field: string) => {
                                clientFieldsObj[field] = true;
                            });
                            this.modifiedClientFields = clientFieldsObj;
                        } else {
                            this.modifiedClientFields = { ...defaultClientFields, ...data.clientFields };
                        }
                    }
                    if (data.fournisseurFields) {
                        if (Array.isArray(data.fournisseurFields)) {
                            const fournisseurFieldsObj: { [key: string]: boolean } = { ...defaultFournisseurFields };
                            data.fournisseurFields.forEach((field: string) => {
                                fournisseurFieldsObj[field] = true;
                            });
                            this.modifiedFournisseurFields = fournisseurFieldsObj;
                        } else {
                            this.modifiedFournisseurFields = { ...defaultFournisseurFields, ...data.fournisseurFields };
                        }
                    }
                    console.log('✅ [loadModifiedFields] Champs chargés depuis localStorage (fallback)');
                } catch (e) {
                    console.error('Erreur lors du chargement depuis localStorage:', e);
                }
            }
        }
    }

    // Vérifier si un champ client est modifié (retourne true si le champ est caché)
    isClientFieldModified(field: string): boolean {
        return this.modifiedClientFields[field] === true;
    }

    // Vérifier si un champ fournisseur est modifié (retourne true si le champ est caché)
    isFournisseurFieldModified(field: string): boolean {
        return this.modifiedFournisseurFields[field] === true;
    }

    async downloadPDF(): Promise<void> {
        if (!this.documentContainer) {
            this.toastr.error('Contenu du document non disponible.');
            return;
        }

        if (!this.document) {
            this.toastr.error('Document non chargé.');
            return;
        }

        this.isExportingPDF = true;
        const documentType = this.document?.typeDocument?.titre || 'Document';
        const documentRef = this.getDisplayReference();
        this.toastr.info(`Génération du PDF ${documentType} ${documentRef} en cours...`, '', {
            timeOut: 0,
            progressBar: true
        });

        try {
            const element = this.documentContainer.nativeElement;
            
            // Masquer les éléments qui ne doivent pas apparaître dans le PDF
            const elementsToHide = element.querySelectorAll('.vertical-buttons, app-parametre-bar, app-title-section');
            const originalDisplays: string[] = [];

            elementsToHide.forEach((el) => {
                const htmlEl = el as HTMLElement;
                originalDisplays.push(htmlEl.style.display || '');
                htmlEl.style.display = 'none';
            });

            this.cdr.detectChanges();
            await new Promise(resolve => setTimeout(resolve, 200));

            // Générer le canvas (mêmes options que liste / aperçu : `document-pdf-export.util`)
            const canvas = await html2canvas(
                element,
                Object.assign({}, getHtml2CanvasOptionsForDocumentExport(element as HTMLElement), {
                    onclone: (clonedDoc: Document) => {
                        const overlays = clonedDoc.querySelectorAll('.vertical-buttons, app-parametre-bar, app-title-section');
                        overlays.forEach(overlay => {
                            (overlay as HTMLElement).style.display = 'none';
                            (overlay as HTMLElement).style.visibility = 'hidden';
                        });
                    }
                })
            );

            // Restaurer l'affichage des éléments
            elementsToHide.forEach((el, index) => {
                (el as HTMLElement).style.display = originalDisplays[index] || '';
            });

            this.cdr.detectChanges();

            const date = new Date().toISOString().split('T')[0];
            const cleanDocumentType = documentType.toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '');
            const cleanDocumentRef = documentRef
                .replace(/[^a-zA-Z0-9]/g, '')
                .substring(0, 20);
            const filename = `${cleanDocumentType}-${cleanDocumentRef}-${date}.pdf`;

            saveCanvasAsPdfDocument(canvas, filename);
            this.toastr.success(`PDF "${documentType} ${documentRef}" téléchargé avec succès !`, 'Succès');

        } catch (error: any) {
            console.error('Erreur lors de la génération du PDF:', error);
            this.toastr.error(`Erreur lors de la génération du PDF: ${error?.message || 'Erreur inconnue'}`, 'Erreur');
        } finally {
            this.isExportingPDF = false;
            this.cdr.detectChanges();
        }
    }

    private getDocumentTypeTitre(): string {
        return this.document?.typeDocument?.titre || this.document?.type_document?.titre || '';
    }

    private normalizeDocumentTypeTitre(): string {
        return String(this.getDocumentTypeTitre())
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private shouldFilterStockByEntrepot(): boolean {
        const type = this.normalizeDocumentTypeTitre();
        return [
            'BON DE LIVRAISON',
            'BON RECEPTION',
            'BON DE RECEPTION',
            'INVENTAIRE',
            'BON TRANSFERT'
        ].includes(type);
    }

    private getEntrepotIdForStockQuantity(): number | null {
        if (!this.shouldFilterStockByEntrepot()) {
            return null;
        }
        return this.document?.entrepot_id ?? this.document?.entrepot?.id ?? null;
    }

    private getStockCacheKey(productId: number, entrepotId?: number | null): string {
        if (this.shouldFilterStockByEntrepot() && entrepotId) {
            return `${productId}_${entrepotId}`;
        }
        return `${productId}`;
    }

    private stockLoadingKeys = new Set<string>();

    shouldShowQuantiteDisponibleColumn(): boolean {
        const type = this.normalizeDocumentTypeTitre();
        return [
            'INVENTAIRE',
            'BON TRANSFERT',
            'BON DE LIVRAISON',
            'BON RECEPTION',
            'BON DE RECEPTION'
        ].includes(type);
    }

    private fetchLineStockQuantity(productId: number): Observable<number> {
        const entrepotId = this.getEntrepotIdForStockQuantity();
        if (this.shouldFilterStockByEntrepot() && entrepotId != null) {
            return this.stockService.getStockDetailsByErpProductId(productId).pipe(
                map((details: any) => {
                    if (!details?.stocks_by_entrepot || !Array.isArray(details.stocks_by_entrepot)) {
                        return 0;
                    }
                    const row = details.stocks_by_entrepot.find(
                        (s: any) =>
                            Number(s.entrepot_id ?? s.entrepotId) === Number(entrepotId)
                    );
                    return row ? Number(row.quantity ?? row.qty ?? 0) : 0;
                }),
                catchError(() => of(0))
            );
        }
        return this.stockService.getTotalStockByErpProductId(productId).pipe(catchError(() => of(0)));
    }

    private setStockQuantityCache(productId: number, _entrepotId: number | null, quantity: number): void {
        const normalized = Number.isFinite(quantity) ? Math.trunc(quantity) : 0;
        this.stockQuantities.set(productId, normalized);
                                            this.cdr.detectChanges();
    }

    private ensureLineStockLoaded(productId: number): void {
        const entrepotId = this.shouldFilterStockByEntrepot()
            ? this.getEntrepotIdForStockQuantity()
            : null;
        if (this.shouldFilterStockByEntrepot() && entrepotId == null) {
            return;
        }
        const cacheKey = this.getStockCacheKey(productId, entrepotId);
        if (this.stockQuantities.has(productId) || this.stockLoadingKeys.has(cacheKey)) {
            return;
        }
        this.stockLoadingKeys.add(cacheKey);
        this.fetchLineStockQuantity(productId).subscribe({
            next: (q) => {
                const n = Number.isFinite(q) ? q : 0;
                this.setStockQuantityCache(productId, entrepotId, n);
                this.stockLoadingKeys.delete(cacheKey);
            },
            error: () => {
                this.setStockQuantityCache(productId, entrepotId, 0);
                this.stockLoadingKeys.delete(cacheKey);
            }
        });
    }

    private parseHiddenFournisseurFields(doc: any): any {
        let hidden = doc?.hidden_fournisseur_fields;
        if (typeof hidden === 'string') {
            try {
                hidden = JSON.parse(hidden);
            } catch {
                hidden = null;
            }
        }
        return hidden && typeof hidden === 'object' ? hidden : null;
    }

    /** ID entrepôt affiché : logiciel-caisse (hidden) puis FK ERP héritée. */
    private resolveLogicielCaisseEntrepotId(doc: any = this.document): number | null {
        const hidden = this.parseHiddenFournisseurFields(doc);
        const fromHidden = Number(hidden?.logiciel_caisse_entrepot_id);
        if (Number.isFinite(fromHidden) && fromHidden > 0) {
            return fromHidden;
        }
        const legacy = Number(doc?.entrepot_id ?? doc?.entrepot?.id);
        return Number.isFinite(legacy) && legacy > 0 ? legacy : null;
    }

    // Charger les quantités disponibles (LC gestion-globale, filtré par entrepôt) — même API que Gestion stock / document.
    private loadStockQuantitiesForAllProducts(): void {
        const lignes = this.document?.lignesDocument || this.document?.lignes_document || [];
        if (!lignes || lignes.length === 0) {
            return;
        }

        const metas: Array<{
            id: number;
            code_barre?: string | null;
            ref_interne?: string | null;
            ref_fabricant?: string | null;
        }> = [];
        const seen = new Set<number>();

        for (const ligne of lignes) {
            const productId = Number(ligne?.product_id);
            if (!Number.isFinite(productId) || productId <= 0 || seen.has(productId)) {
                continue;
            }
            seen.add(productId);
            const code =
                ligne?.code_bar ??
                ligne?.code_barre ??
                null;
            const refInt =
                ligne?.ref_interne ??
                (ligne?.reference && ligne.reference !== '-' && ligne.reference !== '--'
                    ? ligne.reference
                    : null);
            metas.push({
                id: productId,
                code_barre: code,
                ref_interne: refInt,
                ref_fabricant: ligne?.ref_fabricant ?? ligne?.ref_fabriquant ?? null,
            });
        }

        if (!metas.length) {
            return;
        }

        const entrepotIds = this.getApproveLcEntrepotIdsForStock();
        this.lcStockGestion
            .getStockGlobaleByErpProducts(metas, {
                entrepotIds,
            })
            .subscribe({
                next: (rec) => {
                    for (const m of metas) {
                        const pair = rec[m.id] ?? { physique: 0, disponible: 0 };
                        // Colonne « Quantité disponible » = stock_disponible (Gestion stock).
                        this.stockQuantities.set(m.id, Number(pair.disponible) || 0);
                    }
                                this.cdr.detectChanges();
                            },
                error: () => {
                    for (const m of metas) {
                        this.stockQuantities.set(m.id, 0);
                    }
                    this.cdr.detectChanges();
                },
            });
    }

    /** Entrepôt LC du document (hidden / entrepot_id) pour filtrer gestion-globale. */
    private getApproveLcEntrepotIdsForStock(): number[] | undefined {
        const lcId = this.resolveLogicielCaisseEntrepotId(this.document);
        if (lcId != null && Number.isFinite(lcId) && lcId > 0) {
            return [lcId];
        }
        const fallback = Number(this.document?.entrepot_id ?? this.document?.entrepot?.id);
        if (Number.isFinite(fallback) && fallback > 0) {
            return [fallback];
        }
        return undefined;
    }

    private normalizeLigneForApproveDisplay(ligne: any): any {
        const embedded =
            ligne?.produit ??
            ligne?.product ??
            ligne?.product_data ??
            ligne?.produit_data ??
            null;
        const src =
            embedded && typeof embedded === 'object' ? { ...embedded, ...ligne } : ligne;

        const refInterne =
            src.ref_interne ??
            (src.reference && src.reference !== '-' && src.reference !== '--' ? src.reference : null) ??
            src.sku ??
            src.code_article ??
            null;
        const codeBarRaw = src.code_bar ?? src.code_barre ?? null;
        const codeBarTrim = codeBarRaw != null ? String(codeBarRaw).trim() : '';
        const codeBar =
            codeBarTrim === '' || codeBarTrim === '0' ? null : codeBarTrim;

        const couleur =
            src.couleur ??
            src.couleur_libelle ??
            src.libelle_couleur ??
            src.nom_couleur ??
            null;
        const taille =
            src.taille ??
            src.taille_libelle ??
            src.libelle_taille ??
            src.nom_taille ??
            null;
        let refFabRaw = src.ref_fabricant ?? src.ref_fabriquant ?? null;
        let refFabTrim = refFabRaw != null ? String(refFabRaw).trim() : '';
        if (refFabTrim === '' || refFabTrim === '-' || refFabTrim === '—') {
            const libRaw = String(src.libelle ?? src.designation ?? '').trim();
            const libParts = libRaw.match(/^(.+?)\s*\/\s*(\S+)\s*$/);
            if (libParts) {
                refFabTrim = libParts[2].trim();
            }
        }
        const refFabricant =
            refFabTrim === '' || refFabTrim === '-' || refFabTrim === '—' ? null : refFabTrim;

        return {
            ...ligne,
            ref_interne: refInterne,
            reference: refInterne,
            ref_fabricant: refFabricant,
            ref_fabriquant: refFabricant,
            code_bar: codeBar,
            code_barre: codeBar,
            couleur: couleur != null && String(couleur).trim() !== '' ? String(couleur).trim() : null,
            taille: taille != null && String(taille).trim() !== '' ? String(taille).trim() : null
        };
    }


    private ligneNeedsProductEnrichment(ligne: any): boolean {
        if (!ligne?.product_id) {
            return false;
        }
        const pid = Number(ligne.product_id);
        if (!Number.isFinite(pid) || pid <= 0) {
            return false;
        }
        const cb = ligne.code_bar ?? ligne.code_barre;
        const cbTrim = cb != null ? String(cb).trim() : '';
        const cbEmpty = cbTrim === '' || cbTrim === '0';
        const refFab = ligne.ref_fabricant ?? ligne.ref_fabriquant;
        const refFabTrim = refFab != null ? String(refFab).trim() : '';
        const refFabEmpty = refFabTrim === '' || refFabTrim === '-';
        const couleur = String(ligne?.couleur ?? '').trim();
        const taille = String(ligne?.taille ?? '').trim();
        const variantAxesMissing = !couleur || !taille;
        return cbEmpty || refFabEmpty || variantAxesMissing;
    }

    /** Enrichissement catalogue puis construction matricielle (affichage unique, sans flash). */
    private prepareApproveProductsDisplay(): void {
        this.approveProductsReady = false;
        this.approveProductBlocks = [];
        this.cdr.markForCheck();
        this.enrichLignesDocumentFromProducts()
            .pipe(
                finalize(() => {
                    this.rebuildApproveProductBlocks();
                    this.approveProductsReady = true;
                    this.loadStockQuantitiesForAllProducts();
                    this.cdr.detectChanges();
                })
            )
            .subscribe();
    }

    private mergeProductDataIntoLigne(ligne: any, product: any): any {
        const p = product?.data ?? product;
        if (!p || typeof p !== 'object') {
            return ligne;
        }
        const ref =
            p.ref_interne ??
            p.reference ??
            p.sku ??
            p.code_article ??
            null;
        const cb = p.code_barre ?? p.code_bar ?? null;
        const nested = p.produit ?? p.product ?? null;
        const refFab =
            p.ref_fabricant ??
            p.ref_fabriquant ??
            nested?.ref_fabricant ??
            nested?.ref_fabriquant ??
            null;
        return this.normalizeLigneForApproveDisplay({
            ...ligne,
            ref_interne: ligne.ref_interne || ref || nested?.ref_interne || null,
            ref_fabricant: ligne.ref_fabricant || ligne.ref_fabriquant || refFab || null,
            ref_fabriquant: ligne.ref_fabriquant || ligne.ref_fabricant || refFab || null,
            code_bar: ligne.code_bar || cb || null,
            code_barre: ligne.code_barre || cb || null,
            couleur:
                ligne.couleur ||
                p.couleur ||
                p.couleur_libelle ||
                p.libelle_couleur ||
                null,
            taille:
                ligne.taille ||
                p.taille ||
                p.taille_libelle ||
                p.libelle_taille ||
                null
        });
    }

    /**
     * Complète code_bar / ref / couleur / taille depuis le catalogue.
     * Tous les product_id du document sont chargés pour un regroupement matriciel fiable.
     */
    private enrichLignesDocumentFromProducts(): Observable<void> {
        const lignes: any[] = this.document?.lignesDocument || this.document?.lignes_document || [];
        if (!lignes.length) {
            return of(undefined);
        }

        const productIds = [
            ...new Set(
                lignes
                    .map((l) => Number(l.product_id))
                    .filter((id) => !Number.isNaN(id) && id > 0)
            )
        ];
        if (!productIds.length) {
            return of(undefined);
        }

        return forkJoin(
            productIds.map((id) =>
                this.productsService.getDetailsProduct(id).pipe(
                    map((res) => ({ id, data: (res as any)?.data ?? res })),
                    catchError(() => of({ id, data: null }))
                )
            )
        ).pipe(
            map((results) => {
            const byId = new Map(results.map((r) => [r.id, r.data]));
            const enriched = lignes.map((ligne) => {
                const pid = Number(ligne.product_id);
                if (!productIds.includes(pid)) {
                    return ligne;
                }
                const product = byId.get(pid);
                if (!product) {
                    return ligne;
                }
                return this.mergeProductDataIntoLigne(ligne, product);
            });
            if (this.document) {
                this.document.lignesDocument = enriched;
                this.document.lignes_document = enriched;
            }
            })
        );
    }

    private parseLibelleReferenceParts(ligne: any): { base: string; refSuffix: string } | null {
        const lib = String(ligne?.libelle ?? ligne?.designation ?? '').trim();
        const m = lib.match(/^(.+?)\s*\/\s*(\S+)\s*$/);
        if (!m) {
            return null;
        }
        const base = m[1].trim();
        const refSuffix = m[2].trim();
        if (!base || !refSuffix) {
            return null;
        }
        return { base, refSuffix };
    }

    private ligneLooksLikeVariantSku(ligne: any): boolean {
        const c = String(ligne?.couleur ?? '').trim();
        const t = String(ligne?.taille ?? '').trim();
        if (c || t) {
            return true;
        }
        const cb = ligne?.code_bar ?? ligne?.code_barre;
        const s = cb != null ? String(cb).trim() : '';
        return s !== '' && s !== '0' && s !== '--';
    }

    private getApproveLigneGroupKey(ligne: any): string {
        const nested = ligne?.produit ?? ligne?.product ?? ligne?.product_data ?? null;
        const refFab = String(
            ligne?.ref_fabricant ??
                ligne?.ref_fabriquant ??
                nested?.ref_fabricant ??
                nested?.ref_fabriquant ??
                ''
        )
            .trim()
            .toLowerCase();
        if (refFab && refFab !== '-' && refFab !== '—') {
            return `ref:${refFab}`;
        }

        const libRefParts = this.parseLibelleReferenceParts(ligne);
        if (libRefParts) {
            return `libref:${libRefParts.base.toLowerCase()}||${libRefParts.refSuffix.toLowerCase()}`;
        }

        const refIntRaw =
            ligne?.ref_interne ??
            (ligne?.reference && ligne.reference !== '-' && ligne.reference !== '--'
                ? ligne.reference
                : null) ??
            nested?.ref_interne ??
            null;
        const refInt = refIntRaw != null ? String(refIntRaw).trim().toLowerCase() : '';
        if (refInt && refInt !== '—' && refInt !== '--' && !this.ligneLooksLikeVariantSku(ligne)) {
            return `refint:${refInt}`;
        }

        const libFull = String(ligne?.libelle ?? ligne?.designation ?? '')
            .trim()
            .toLowerCase();
        if (libFull) {
            const baseOnly = libFull.split(/\s*\/\s*/)[0].trim();
            return baseOnly ? `lib:${baseOnly}` : `lib:${libFull}`;
        }
        const pid = ligne?.product_id != null ? Number(ligne.product_id) : NaN;
        if (Number.isFinite(pid) && pid > 0) {
            return `pid:${pid}`;
        }
        return `solo:${Math.random()}`;
    }

    private getApproveLigneCouleur(ligne: any): string {
        const c = ligne?.couleur != null ? String(ligne.couleur).trim() : '';
        return c || '—';
    }

    private getApproveLigneTaille(ligne: any): string {
        const t = ligne?.taille != null ? String(ligne.taille).trim() : '';
        return t || '—';
    }

    private approveMatrixCellKey(couleur: string, taille: string): string {
        return `${String(couleur || '—').trim().toLowerCase()}||${String(taille || '—').trim().toLowerCase()}`;
    }

    private buildApproveClassicProductBlocks(lignes: any[]): ApproveProductDisplayBlock[] {
        return lignes.map((ligne) => ({ type: 'single' as const, ligne }));
    }

    /**
     * Construit l’affichage produits : matrice couleur (lignes) × taille (colonnes)
     * pour les groupes multi-variantes, sinon ligne simple.
     */
    rebuildApproveProductBlocks(): void {
        const lignes: any[] = this.document?.lignesDocument || this.document?.lignes_document || [];
        if (!Array.isArray(lignes) || lignes.length === 0) {
            this.approveProductBlocks = [];
            return;
        }

        if (!this.useProductMatrixLineDisplay()) {
            this.approveProductBlocks = this.buildApproveClassicProductBlocks(lignes);
            this.calculateTotalsFromDocument();
            return;
        }

        const order: string[] = [];
        const groups = new Map<string, any[]>();
        for (const ligne of lignes) {
            const key = this.getApproveLigneGroupKey(ligne);
            if (!groups.has(key)) {
                order.push(key);
                groups.set(key, []);
            }
            groups.get(key)!.push(ligne);
        }

        const collator = (a: string, b: string) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        const blocks: ApproveProductDisplayBlock[] = [];

        for (const key of order) {
            const group = groups.get(key) || [];
            if (group.length === 0) {
                continue;
            }

            const hasVariantAxes = group.some((l) => {
                const c = String(l?.couleur ?? '').trim();
                const t = String(l?.taille ?? '').trim();
                return !!c || !!t;
            });

            if (group.length === 1 && !hasVariantAxes) {
                blocks.push({ type: 'single', ligne: group[0] });
                continue;
            }

            if (group.length === 1 && hasVariantAxes) {
                // Une seule variante : afficher quand même en matrice 1×1 pour cohérence.
            }

            const couleurs: string[] = [];
            const tailles: string[] = [];
            const couleurSeen = new Set<string>();
            const tailleSeen = new Set<string>();
            const cellMap: { [k: string]: any } = {};
            let totalQnt = 0;
            let totalHT = 0;
            let stockSum = 0;
            const ajoutLignes: any[] = [];

            for (const l of group) {
                const c = this.getApproveLigneCouleur(l);
                const t = this.getApproveLigneTaille(l);
                const ck = c.toLowerCase();
                const tk = t.toLowerCase();
                if (!couleurSeen.has(ck)) {
                    couleurSeen.add(ck);
                    couleurs.push(c);
                }
                if (!tailleSeen.has(tk)) {
                    tailleSeen.add(tk);
                    tailles.push(t);
                }
                const cellKey = this.approveMatrixCellKey(c, t);
                const q = parseFloat(String(l?.qnt ?? 0)) || 0;
                const prev = cellMap[cellKey];
                if (!prev) {
                    cellMap[cellKey] = { ...l, qnt: q };
                } else {
                    const prevQ = parseFloat(String(prev?.qnt ?? 0)) || 0;
                    cellMap[cellKey] = { ...prev, qnt: prevQ + q };
                }
                const pu = this.isAchat()
                    ? this.getPrixAchatLigneApprove(l)
                    : parseFloat(String(l?.prix_ht ?? 0)) || 0;
                totalQnt += q;
                totalHT += pu * q;
                stockSum += this.getStockQuantity(l?.product_id, l);
                if (Array.isArray(l?.ajout_ligne) && l.ajout_ligne.length) {
                    ajoutLignes.push(...l.ajout_ligne);
                }
            }

            const first = group[0];
            const refInterneRaw =
                first?.ref_interne ??
                (first?.reference && first.reference !== '-' && first.reference !== '--'
                    ? first.reference
                    : null);
            const libParts = this.parseLibelleReferenceParts(first);
            const libelleBlock =
                libParts?.base ||
                String(first?.libelle ?? first?.designation ?? '').trim() ||
                '--';
            const refFabBlock =
                String(first?.ref_fabricant ?? first?.ref_fabriquant ?? '').trim() ||
                libParts?.refSuffix ||
                '';
            blocks.push({
                type: 'matrix',
                libelle: libelleBlock,
                refFabricant: refFabBlock,
                refInterne: refInterneRaw != null ? String(refInterneRaw).trim() : '',
                couleurs: couleurs.sort(collator),
                tailles: tailles.sort(collator),
                cellMap,
                totalQnt,
                totalHT,
                unite: first?.unite || '--',
                prixUnitaire: this.isAchat()
                    ? this.getPrixAchatLigneApprove(first)
                    : parseFloat(String(first?.prix_ht ?? 0)) || 0,
                tva: parseFloat(String(first?.tva ?? 0)) || 0,
                remise: parseFloat(String(first?.remise ?? 0)) || 0,
                stockSum,
                ajoutLignes
            });
        }

        this.approveProductBlocks = blocks;
    }

    hasApproveMatrixCell(block: ApproveProductDisplayBlock, couleur: string, taille: string): boolean {
        if (!block?.cellMap) {
            return false;
        }
        return !!block.cellMap[this.approveMatrixCellKey(couleur, taille)];
    }

    getApproveMatrixCell(
        block: ApproveProductDisplayBlock,
        couleur: string,
        taille: string
    ): any | null {
        if (!block?.cellMap) {
            return null;
        }
        return block.cellMap[this.approveMatrixCellKey(couleur, taille)] || null;
    }

    getApproveMatrixCellQty(
        block: ApproveProductDisplayBlock,
        couleur: string,
        taille: string
    ): number {
        const ligne = this.getApproveMatrixCell(block, couleur, taille);
        if (!ligne) {
            return 0;
        }
        return parseFloat(String(ligne.qnt ?? 0)) || 0;
    }

    /** Référence produit (ref_fabricant prioritaire) pour la colonne RÉFÉRENCE. */
    getLigneReferenceDisplay(ligne: any): string {
        const refFab = ligne?.ref_fabricant ?? ligne?.ref_fabriquant;
        const refFabTrim = refFab != null ? String(refFab).trim() : '';
        if (refFabTrim && refFabTrim !== '-') {
            return refFabTrim;
        }
        const ref =
            ligne?.ref_interne ??
            (ligne?.reference && ligne.reference !== '-' && ligne.reference !== '--'
                ? ligne.reference
                : null);
        const s = ref != null ? String(ref).trim() : '';
        return s || '--';
    }

    getApproveBlockReference(block: ApproveProductDisplayBlock): string {
        if (block.type === 'single') {
            return this.getLigneReferenceDisplay(block.ligne);
        }
        const refFab = (block.refFabricant || '').trim();
        if (refFab && refFab !== '-') {
            return refFab;
        }
        const refInt = (block.refInterne || '').trim();
        return refInt || '--';
    }

    getApproveBlockDesignation(block: ApproveProductDisplayBlock): string {
        if (block.type === 'single') {
            const lib = block.ligne?.libelle != null ? String(block.ligne.libelle).trim() : '';
            return lib || '';
        }
        return (block.libelle || '').trim();
    }

    /** Quantité entière sans séparateur de milliers (ex. 2500, pas 2,500). */
    formatQtyPlain(value: number | string | null | undefined): string {
        const n = Number(value);
        if (!Number.isFinite(n)) {
            return '0';
        }
        return String(Math.trunc(n));
    }

    getApproveBlockTitle(block: ApproveProductDisplayBlock): string {
        if (block.type === 'single') {
            return this.getLigneLibelleAvecRefFabricant(block.ligne);
        }
        const lib = block.libelle || '--';
        const ref = (block.refFabricant || '').trim();
        if (!ref || ref === '-') {
            return lib;
        }
        return `${lib} / ${ref}`;
    }

    getApproveBlockStockDisplay(block: ApproveProductDisplayBlock): string {
        if (block.type === 'single') {
            return this.getStockQuantityForDisplay(block.ligne);
        }
        let sum = 0;
        const map = block.cellMap || {};
        for (const k of Object.keys(map)) {
            const l = map[k];
            sum += this.getStockQuantity(l?.product_id, l);
        }
        return String(Math.trunc(sum));
    }

    /** Code-barres affiché sous la désignation. */
    getLigneCodeBarDisplay(ligne: any): string {
        const v = ligne?.code_bar ?? ligne?.code_barre;
        if (v == null) {
            return '--';
        }
        const s = String(v).trim();
        if (s === '' || s === '0') {
            return '--';
        }
        return s;
    }

    /** Libellé + « / réf. fabricant » sur la même ligne (sans ref_interne). */
    getLigneLibelleAvecRefFabricant(ligne: any): string {
        const libelle = ligne?.libelle != null ? String(ligne.libelle).trim() : '';
        const refFab = ligne?.ref_fabricant ?? ligne?.ref_fabriquant;
        const refFabTrim = refFab != null ? String(refFab).trim() : '';
        if (!refFabTrim || refFabTrim === '-') {
            return libelle || '--';
        }
        return libelle ? `${libelle} / ${refFabTrim}` : refFabTrim;
    }

    /** Ligne secondaire sous le code-barres : taille / couleur. */
    getLigneCouleurTailleDisplay(ligne: any): string {
        const t = ligne?.taille != null ? String(ligne.taille).trim() : '';
        const c = ligne?.couleur != null ? String(ligne.couleur).trim() : '';
        if (!t && !c) {
            return '';
        }
        if (t && c) {
            return `${t} / ${c}`;
        }
        return t || c;
    }

    /** Prix d'achat ligne document (facture fournisseur) pour l'aperçu ; repli prix_ht. */
    getPrixAchatLigneApprove(ligne: any): number {
        const raw = ligne?.prix_achat ?? ligne?.prixAchat;
        if (raw != null && raw !== '') {
            const n = parseFloat(String(raw));
            if (!isNaN(n)) {
                return n;
            }
        }
        const fb = ligne?.prix_ht;
        if (fb != null && fb !== '') {
            const n2 = parseFloat(String(fb));
            if (!isNaN(n2)) {
                return n2;
            }
        }
        return 0;
    }

    // Obtenir la quantité disponible pour un produit
    getStockQuantity(productId: number | null | undefined, ligne?: any): number {
        if (!productId) {
            return 0;
        }
        const quantity = this.stockQuantities.get(Number(productId));
        return quantity !== undefined && Number.isFinite(quantity) ? quantity : 0;
    }

    getStockQuantityForDisplay(ligne: any): string {
        const qty = this.getStockQuantity(ligne?.product_id, ligne);
        if (!Number.isFinite(qty)) {
            return '0';
        }
        return String(Math.trunc(qty));
    }

    // Trier les ajout_ligne par leur propriété order
    getSortedAjoutLignes(ajoutLignes: any[] | null | undefined): any[] {
        if (!ajoutLignes || !Array.isArray(ajoutLignes)) {
            return [];
        }
        return [...ajoutLignes].sort((a, b) => {
            const orderA = a.order !== undefined && a.order !== null ? Number(a.order) : 0;
            const orderB = b.order !== undefined && b.order !== null ? Number(b.order) : 0;
            return orderA - orderB;
        });
    }

    // Vérifier si un ajout_ligne est un séparateur
    isSeparator(ajoutLigne: any): boolean {
        if (!ajoutLigne) return false;
        return ajoutLigne.separateur === true || ajoutLigne.separateur === 1 || ajoutLigne.separateur === 'true';
    }

    // Vérifier si un ajout_ligne est une rubrique
    isRubrique(ajoutLigne: any): boolean {
        if (!ajoutLigne || this.isSeparator(ajoutLigne)) return false;
        const sousTotal = ajoutLigne.sous_total;
        const hasSubtotal = sousTotal !== null && sousTotal !== undefined && Number(sousTotal) > 0;
        if (hasSubtotal) return false;
        return ajoutLigne.rubrique && String(ajoutLigne.rubrique).trim().length > 0;
    }

    // Vérifier si un ajout_ligne est un texte
    isText(ajoutLigne: any): boolean {
        if (!ajoutLigne || this.isSeparator(ajoutLigne)) return false;
        const sousTotal = ajoutLigne.sous_total;
        const hasSubtotal = sousTotal !== null && sousTotal !== undefined && Number(sousTotal) > 0;
        if (hasSubtotal) return false;
        const hasRubrique = ajoutLigne.rubrique && String(ajoutLigne.rubrique).trim().length > 0;
        if (hasRubrique) return false;
        return ajoutLigne.contenu_text && String(ajoutLigne.contenu_text).trim().length > 0;
    }

    // Vérifier si un ajout_ligne est un sous-total
    isSubtotal(ajoutLigne: any): boolean {
        if (!ajoutLigne || this.isSeparator(ajoutLigne)) return false;
        const sousTotal = ajoutLigne.sous_total;
        if (sousTotal === null || sousTotal === undefined) return false;
        const numValue = Number(sousTotal);
        return !isNaN(numValue) && numValue > 0;
    }

    /**
     * Charge parametrage_doc depuis le document
     */
    private loadParametrageDoc(doc: any): void {
        if (doc.parametrage_doc) {
            const params = typeof doc.parametrage_doc === 'string' ? JSON.parse(doc.parametrage_doc) : doc.parametrage_doc;
            this.documentColumnSettings.aff_montant_ttc = params.aff_montant_ttc ?? params.afficherMontantsTTC ?? false;
            this.documentColumnSettings.aff_code_bar = params.aff_code_bar ?? params.afficherCodeBarres ?? true;
            this.documentColumnSettings.aff_date_echeance = params.aff_date_echeance ?? params.aff_date_échéance ?? params.afficherDateEcheance ?? false;
            this.documentColumnSettings.aff_date_liv = params.aff_date_liv ?? params.afficherDateLivraison ?? true;
            this.documentColumnSettings.aff_montant_total = params.aff_montant_total ?? params.afficherMontantTotal ?? true;
            this.documentColumnSettings.aff_coordonnees = params.aff_coordonnees ?? params.aff_coordonnées ?? params.afficherCoordonneesClients ?? true;
            this.documentColumnSettings.reference = params.reference ?? params.référence ?? false;
            this.documentColumnSettings.quantite = params.quantite ?? params.quantité ?? true;
            this.documentColumnSettings.unite = params.unite ?? params.unité ?? false;
            this.documentColumnSettings.taille = params.taille ?? false;
            this.documentColumnSettings.prix_unitaire = params.prix_unitaire ?? params.prixUnitaire ?? true;
            this.documentColumnSettings.prixUnitaire = this.documentColumnSettings.prix_unitaire;
            this.documentColumnSettings.tva = params.tva ?? true;
            this.documentColumnSettings.remise = params.remise ?? false;
            
            // Synchroniser avec les noms frontend (pour compatibilité)
            this.documentColumnSettings.afficherMontantsTTC = this.documentColumnSettings.aff_montant_ttc;
            this.documentColumnSettings.afficherCodeBarres = this.documentColumnSettings.aff_code_bar;
            this.documentColumnSettings.afficherDateEcheance = this.documentColumnSettings.aff_date_echeance;
            this.documentColumnSettings.afficherDateLivraison = this.documentColumnSettings.aff_date_liv;
            this.documentColumnSettings.afficherMontantTotal = this.documentColumnSettings.aff_montant_total;
            this.documentColumnSettings.afficherCoordonneesClients = this.documentColumnSettings.aff_coordonnees;
            
            console.log('✅ [loadParametrageDoc] Paramètres chargés:', this.documentColumnSettings);
        }
    }

    /**
     * Gère les changements de paramètres depuis parametre-bar
     */
    onDocumentSettingsChange(settings: any): void {
        const onSaveComplete = settings?.__onSaveComplete as
            | ((result: { success: boolean; message?: string }) => void)
            | undefined;

        if (settings.reference !== undefined) {
            this.documentColumnSettings.reference = settings.reference;
        }
        if (settings.quantite !== undefined) {
            this.documentColumnSettings.quantite = settings.quantite;
        }
        if (settings.unite !== undefined) {
            this.documentColumnSettings.unite = settings.unite;
        }
        if (settings.taille !== undefined) {
            this.documentColumnSettings.taille = settings.taille;
        }
        if (settings.prixUnitaire !== undefined) {
            this.documentColumnSettings.prixUnitaire = settings.prixUnitaire;
            this.documentColumnSettings.prix_unitaire = settings.prixUnitaire;
        }
        if (settings.tva !== undefined) {
            this.documentColumnSettings.tva = settings.tva;
        }
        if (settings.remise !== undefined) {
            this.documentColumnSettings.remise = settings.remise;
        }
        if (settings.afficherMontantsTTC !== undefined) {
            this.documentColumnSettings.afficherMontantsTTC = settings.afficherMontantsTTC;
            this.documentColumnSettings.aff_montant_ttc = settings.afficherMontantsTTC;
        }
        if (settings.afficherCodeBarres !== undefined) {
            this.documentColumnSettings.afficherCodeBarres = settings.afficherCodeBarres;
            this.documentColumnSettings.aff_code_bar = settings.afficherCodeBarres;
        }
        if (settings.afficherDateEcheance !== undefined) {
            this.documentColumnSettings.afficherDateEcheance = settings.afficherDateEcheance;
            this.documentColumnSettings.aff_date_echeance = settings.afficherDateEcheance;
        }
        if (settings.afficherDateLivraison !== undefined) {
            this.documentColumnSettings.afficherDateLivraison = settings.afficherDateLivraison;
            this.documentColumnSettings.aff_date_liv = settings.afficherDateLivraison;
        }
        if (settings.afficherMontantTotal !== undefined) {
            this.documentColumnSettings.afficherMontantTotal = settings.afficherMontantTotal;
            this.documentColumnSettings.aff_montant_total = settings.afficherMontantTotal;
        }
        if (settings.afficherCoordonneesClients !== undefined) {
            this.documentColumnSettings.afficherCoordonneesClients = settings.afficherCoordonneesClients;
            this.documentColumnSettings.aff_coordonnees = settings.afficherCoordonneesClients;
        }
        if (settings.afficherProduitsMatriciel !== undefined) {
            this.paramDocAffProduitMatriciel = isTruthyTinyInt(settings.afficherProduitsMatriciel);
            this.rebuildApproveProductBlocks();
        }
        // Mettre à jour color, label, date_echeance (fréquence) et nbr_date depuis parametrage_doc si fournis
        if (settings.parametrage_doc) {
            if (settings.parametrage_doc.color !== undefined) {
                this.primaryColor = settings.parametrage_doc.color;
            }
            if (settings.parametrage_doc.label !== undefined && settings.parametrage_doc.label !== '') {
                this.documentLabel = settings.parametrage_doc.label;
            }
            // date_echeance contient maintenant la fréquence (jours/mois/années)
            if (settings.parametrage_doc.date_echeance !== undefined) {
                const frequencyValue = settings.parametrage_doc.date_echeance as string;
                if (frequencyValue === 'jours' || frequencyValue === DateEcheanceFrequency.JOURS) {
                    this.dateFrequency = DateEcheanceFrequency.JOURS;
                } else if (frequencyValue === 'mois' || frequencyValue === DateEcheanceFrequency.MOIS) {
                    this.dateFrequency = DateEcheanceFrequency.MOIS;
                } else if (frequencyValue === 'annee' || frequencyValue === 'années' || frequencyValue === DateEcheanceFrequency.ANNEE) {
                    this.dateFrequency = DateEcheanceFrequency.ANNEE;
                } else {
                    this.dateFrequency = DateEcheanceFrequency.JOURS; // Valeur par défaut
                }
            }
            // nbr_date est maintenant dans parametrage_doc
            if (settings.parametrage_doc.nbr_date !== undefined) {
                this.nbrDate = settings.parametrage_doc.nbr_date;
            }
        }
        // Fallback: si nbr_date est encore au niveau racine (pour compatibilité)
        if (settings.nbr_date !== undefined && (!settings.parametrage_doc || settings.parametrage_doc.nbr_date === undefined)) {
            this.nbrDate = settings.nbr_date;
        }
        if (settings?.__saveParametrageDoc !== true) {
            return;
        }
        this.updateDocumentParametrage(
            (success, message) => {
                onSaveComplete?.({ success, message });
            },
            { skipSuccessToast: true }
        );
    }

    /**
     * Met à jour le document avec parametrage_doc via PUT
     */
    private updateDocumentParametrage(
        onComplete?: (success: boolean, message?: string) => void,
        options?: { skipSuccessToast?: boolean }
    ): void {
        if (!this.documentId) {
            console.warn('⚠️ [updateDocumentParametrage] documentId manquant, impossible de mettre à jour');
            onComplete?.(false, 'Document introuvable.');
            return;
        }
        
        if (!this.document) {
            console.warn('⚠️ [updateDocumentParametrage] document non chargé, impossible de mettre à jour');
            onComplete?.(false, 'Document non chargé.');
            return;
        }
        
        const parametrageDoc = this.buildParametrageDoc();
        
        // Déterminer si c'est un document d'achat ou de vente
        const isAchat = this.isAchat();
        
        // Extraire client_id ou fournisseur_id selon le type de document
        let clientId: number | null = null;
        let fournisseurId: number | null = null;
        
        if (isAchat) {
            // Pour les documents d'achat, utiliser fournisseur_id
            fournisseurId = this.document.fournisseur_id 
                || this.document.fournisseur_data?.id 
                || this.document.fournisseur_data?.fournisseur_id
                || (this.document.fornisseurs && this.document.fornisseurs.length > 0 ? this.document.fornisseurs[0].id : null)
                || null;
        } else {
            // Pour les documents de vente, utiliser client_id
            clientId = this.document.client_id 
                || this.document.client_data?.id 
                || this.document.client_data?.client_id
                || (this.document.clients && this.document.clients.length > 0 ? this.document.clients[0].id : null)
                || null;
        }
        
        // Inclure les champs obligatoires requis par l'API
        const payload: any = {
            user_id: this.document.user_id || this.currentUser.id,
            station_id: this.stationId,
            type_document_id: this.document.type_document_id || this.document.typeDocument?.id,
            statut_id: this.document.statut_id || this.document.statut?.id,
            parametrage_doc: parametrageDoc, // Contient color, label, date_echeance (fréquence) et nbr_date
            ...(this.document.reference_model_type != null && { reference_model_type: this.document.reference_model_type })
        };
        
        // Ajouter client_id ou fournisseur_id selon le type de document
        if (isAchat && fournisseurId) {
            payload.fournisseur_id = fournisseurId;
        } else if (!isAchat && clientId) {
            payload.client_id = clientId;
        }
        
        // Log pour déboguer
        console.log('🟡 [updateDocumentParametrage] Document:', {
            isAchat,
            client_id: this.document.client_id,
            fournisseur_id: this.document.fournisseur_id,
            client_data: this.document.client_data,
            fournisseur_data: this.document.fournisseur_data,
            clients: this.document.clients,
            fornisseurs: this.document.fornisseurs
        });
        console.log('🟡 [updateDocumentParametrage] IDs extraits:', { clientId, fournisseurId });
        console.log('🟡 [updateDocumentParametrage] Mise à jour du document avec parametrage_doc:', parametrageDoc);
        console.log('🟡 [updateDocumentParametrage] Payload complet:', payload);
        
        this.documentService.update(this.documentId, payload).subscribe({
            next: () => {
                if (!options?.skipSuccessToast) {
                    this.toastr.success('Paramètres du document mis à jour', 'Succès');
                }
                onComplete?.(true);
            },
            error: (err) => {
                const apiMsg =
                    err?.error?.message ||
                    (Array.isArray(err?.error?.errors) ? err.error.errors.join(', ') : null) ||
                    err?.message;
                const message = apiMsg || 'Erreur lors de la mise à jour des paramètres';
                if (!options?.skipSuccessToast) {
                    this.toastr.error(message, 'Erreur');
                }
                onComplete?.(false, message);
            }
        });
    }

    /**
     * Calcule le nombre de colonnes visibles dans le tableau
     */
    getTableColumnCount(): number {
        let count = 1; // DÉSIGNATION / RÉFÉRENCE (code-barres inclus)
        if (this.documentColumnSettings.quantite) count++;
        if (this.documentColumnSettings.unite) count++;
        if (this.documentColumnSettings.prixUnitaire) count++;
        if (this.documentColumnSettings.tva) count++;
        if (this.documentColumnSettings.remise) count++;
        if (this.shouldShowQuantiteDisponibleColumn()) count++;
        count++; // TOTAL HT (toujours visible)
        return count;
    }

    /**
     * Construit l'objet parametrage_doc avec tous les paramètres d'affichage
     */
    private buildParametrageDoc(): any {
        const parametrageDoc = {
            aff_montant_ttc: this.documentColumnSettings.aff_montant_ttc !== undefined 
                ? this.documentColumnSettings.aff_montant_ttc 
                : (this.documentColumnSettings.afficherMontantsTTC !== undefined ? this.documentColumnSettings.afficherMontantsTTC : false),
            aff_code_bar: this.documentColumnSettings.aff_code_bar !== undefined 
                ? this.documentColumnSettings.aff_code_bar 
                : (this.documentColumnSettings.afficherCodeBarres !== undefined ? this.documentColumnSettings.afficherCodeBarres : true),
            aff_date_echeance: this.documentColumnSettings.aff_date_echeance !== undefined 
                ? this.documentColumnSettings.aff_date_echeance 
                : (this.documentColumnSettings.afficherDateEcheance !== undefined ? this.documentColumnSettings.afficherDateEcheance : false),
            aff_date_liv: this.documentColumnSettings.aff_date_liv !== undefined 
                ? this.documentColumnSettings.aff_date_liv 
                : (this.documentColumnSettings.afficherDateLivraison !== undefined ? this.documentColumnSettings.afficherDateLivraison : true),
            aff_montant_total: this.documentColumnSettings.aff_montant_total !== undefined 
                ? this.documentColumnSettings.aff_montant_total 
                : (this.documentColumnSettings.afficherMontantTotal !== undefined ? this.documentColumnSettings.afficherMontantTotal : true),
            aff_coordonnees: this.documentColumnSettings.aff_coordonnees !== undefined 
                ? this.documentColumnSettings.aff_coordonnees 
                : (this.documentColumnSettings.afficherCoordonneesClients !== undefined ? this.documentColumnSettings.afficherCoordonneesClients : true),
            reference: this.documentColumnSettings.reference !== undefined 
                ? this.documentColumnSettings.reference 
                : false,
            quantite: this.documentColumnSettings.quantite !== undefined 
                ? this.documentColumnSettings.quantite 
                : true,
            unite: this.documentColumnSettings.unite !== undefined 
                ? this.documentColumnSettings.unite 
                : false,
            taille: this.documentColumnSettings.taille !== undefined
                ? this.documentColumnSettings.taille
                : false,
            prix_unitaire: this.documentColumnSettings.prix_unitaire !== undefined 
                ? this.documentColumnSettings.prix_unitaire 
                : (this.documentColumnSettings.prixUnitaire !== undefined ? this.documentColumnSettings.prixUnitaire : true),
            tva: this.documentColumnSettings.tva !== undefined ? this.documentColumnSettings.tva : true,
            remise: this.documentColumnSettings.remise !== undefined ? this.documentColumnSettings.remise : false,
            // Ajouter color, label, date_echeance (fréquence) et nbr_date dans parametrage_doc
            color: this.primaryColor,
            label: this.documentLabel || '',
            date_echeance: String(this.dateFrequency || DateEcheanceFrequency.JOURS), // Fréquence sélectionnée: jours, mois, ou annee (converti en string pour l'API)
            nbr_date: this.nbrDate
        };
        console.log('🔵 [buildParametrageDoc] parametrage_doc construit avec color, label et date_echeance (fréquence):', JSON.stringify(parametrageDoc, null, 2));
        return parametrageDoc;
    }

}
